import React, { useState, useRef, useEffect } from 'react';
import { Upload, Send, Check, X, Loader2, ChevronLeft, ChevronRight, Edit3, Bot, Sparkles, SkipBack, SkipForward, Maximize2, Save, Wand2, AlertCircle, CheckCircle2, XCircle, Info, MessageSquare, Mic, Square, CircleHelp, Globe, ExternalLink, RefreshCcw, History } from 'lucide-react';
import { API_URL, API_URL_AGENT } from './constants';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import DownloadDropdown from './DownloadDropdown';
import SearchIntegrationCheckbox from './SearchIntegrationBox';
import { useNavigate, useLocation } from 'react-router-dom';
import { saveAs } from 'file-saver';
import RequestReviewPopup from './RequestReviewPopup';
import FeedbackPopup from './FeedbackPopup';
import { createAudioRecordingHandler, getMicrophoneErrorMessage, stopRecording as stopAudioRecording } from './AudioService';
import RightSidebar from './RightSidebar';
import DataGrid from './DataGrid';
import ConversationalGrid from './ConversationalGrid';
import NestedJSONGrid from './NestedJSONGrid';
import NestedJSONViewerPage from './NestedJSONViewerPage';
import { callPythonAPI, callClarificationAPI, requestDownload } from './APICalls';
import { handleResponseError } from '../utils/errorHandler';
import { readCSVHeader } from '../utils/csvUtils';
import { useUserDetails } from './UserDetailsContext';
import { getPlanDetails } from '../utils/PricingPlans';
import UpgradePopup from './UpgradePopup';
import TawkToChat from './TawkToChat';
import LoadingSpinner from './LoadingSpinner';
import FormatSelectionModal from './FormatSelectionModal';
import Auth from './Auth';
import DataAnalysisPopup, { QualityReportInline } from './AIQualityEvaluation';
import RazorpayCheckout from './RazorPayCheckOut';
import PaymentStatus from './PaymentStatus';

const CSVEditor = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useUserDetails();
  const userPlan = getPlanDetails(userDetails?.plan);

  const [showAuthModal, setShowAuthModal] = useState(false);

  // Upgrade Popup State
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  // Search Sources State
  const [searchResults, setSearchResults] = useState([]);
  const [showSourcesModal, setShowSourcesModal] = useState(false);

  const [formatType, setFormatType] = useState('tabular');

  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [pendingChanges, setPendingChanges] = useState({ added: [], updated: [], deleted: [] });
  const [previewMode, setPreviewMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const fileInputRef = useRef(null);
  const contextInputRef = useRef(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [duplicatesInfo, setDuplicatesInfo] = useState(null);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);

  // New states for editing
  const [editingCell, setEditingCell] = useState(null); // { rowId, column }
  const [editValue, setEditValue] = useState('');
  const [manualChanges, setManualChanges] = useState({}); // Track manual edits

  const [showSimilarityAnalysis, setShowSimilarityAnalysis] = useState(false);
  const [similarityThreshold, setSimilarityThreshold] = useState(80);
  const [similarRows, setSimilarRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowEditInput, setRowEditInput] = useState('');
  const [isRowProcessing, setIsRowProcessing] = useState(false);

  // States for Generate Data feature
  const [userSelectedFormat, setUserSelectedFormat] = useState('Flat_Tabular');
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [generateNumber, setGenerateNumber] = useState(100);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [columnNames, setColumnNames] = useState(''); // Column names for generation
  const [contextFile, setContextFile] = useState(null); // Context file for generation
  const [isDedicatedJSONView, setIsDedicatedJSONView] = useState(false);

  // Custom notification state
  const [notification, setNotification] = useState(null);

  // Interactive Generation State
  const [isInteractiveGeneration, setIsInteractiveGeneration] = useState(false);

  // Request Review state
  const [isRequestReview, setIsRequestReview] = useState(false);

  // Search Context State
  const [includeSearch, setIncludeSearch] = useState(false);

  // Refs for current values (needed to avoid stale closures in chat action buttons)
  const contextFileRef = useRef(null);
  const includeSearchRef = useRef(false);

  useEffect(() => {
    contextFileRef.current = contextFile;
  }, [contextFile]);

  useEffect(() => {
    includeSearchRef.current = includeSearch;
  }, [includeSearch]);

  // Feedback Popup state
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);

  // Refinement State
  const [isRefiningPrompt, setIsRefiningPrompt] = useState(false);

  // Voice Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Tour State
  const [runTour, setRunTour] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);

  const [isAIEvalOpen, setIsAIEvalOpen] = useState(false);
  const [aievaluation, setAIEvaluation] = useState('');
  const [activityId, setActivityId] = useState(null);
  const [lastUsedPrompt, setLastUsedPrompt] = useState('');
  const [isAIEvalLoading, setIsAIEvalLoading] = useState(false);
  const [showPremiumDownloadModal, setShowPremiumDownloadModal] = useState(false);
  const [pendingDownloadFormat, setPendingDownloadFormat] = useState(null);
  const [hasPaidPremiumDownload, setHasPaidPremiumDownload] = useState(false);
  const [isGeneratedData, setIsGeneratedData] = useState(false);

  // Track original file format for proper export
  const [originalFileFormat, setOriginalFileFormat] = useState(null); // 'json', 'jsonl', 'csv', or 'jsonl-chat'

  const csvDataRef = useRef([]);
  const headersRef = useRef([]);
  useEffect(() => {
    csvDataRef.current = csvData;
  }, [csvData]);

  useEffect(() => {
    headersRef.current = headers;
  }, [headers]);

  // Track streaming state for delta updates
  const streamingStateRef = useRef({ addedCount: 0 });

  // Pre-load staged progress (shown before first data row arrives)
  const [genLoadingStep, setGenLoadingStep] = useState(0);
  const genPreloadIntervalRef = useRef(null);

  // Auto-start tour on first visit
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('srishti_tour_completed');
    if (!hasSeenTour) {
      // Small delay to ensure UI is fully rendered
      const timer = setTimeout(() => {
        setRunTour(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle loading past activity from state (Data History page)
  useEffect(() => {
    if (location.state && location.state.activity) {
      const activity = location.state.activity;

      // Load prompt metadata
      if (activity.prompt) setGeneratePrompt(activity.prompt);
      if (activity.col_names) setColumnNames(activity.col_names);
      if (activity.number) setGenerateNumber(activity.number);

      // Load actual data if passed from History page
      if (location.state.loadedData && Array.isArray(location.state.loadedData)) {
        // Re-map with _rowId for the grid
        const dataWithIds = location.state.loadedData.map((row, idx) => ({
          ...row,
          _rowId: row._rowId || (idx + 1)
        }));
        setCsvData(dataWithIds);

        if (location.state.loadedHeaders) {
          setHeaders(location.state.loadedHeaders);
        } else if (dataWithIds.length > 0) {
          setHeaders(Object.keys(dataWithIds[0]).filter(k => k !== '_rowId'));
        }
      }

      // Clear location state to prevent reload on every mount/refresh
      window.history.replaceState({}, document.title);

      // Notify the user
      const message = location.state.loadedData
        ? `Successfully reloaded ${location.state.loadedData.length} rows and your settings!`
        : 'Past data session settings loaded! Rows not found, but you can regenerate.';

      setNotification({
        type: 'success',
        message: message
      });

      // Close chat if open to show the inputs
      setIsChatOpen(false);
    }
  }, [location, setGeneratePrompt, setColumnNames, setGenerateNumber, setNotification, setIsChatOpen, setCsvData, setHeaders]);

  // Exit Intent Feedback logic
  useEffect(() => {
    // Only set up if they have data and haven't seen feedback yet
    if (csvData.length === 0) return;

    const handleExitIntent = (e) => {
      // clientY < 10 typically means mouse moved to address bar/tabs
      if (e.clientY <= 10) {
        const hasSeen = localStorage.getItem('srishti_feedback_shown');
        if (!hasSeen) {
          setShowFeedbackPopup(true);
          localStorage.setItem('srishti_feedback_shown', 'true');
        }
      }
    };

    document.addEventListener('mouseleave', handleExitIntent);
    return () => document.removeEventListener('mouseleave', handleExitIntent);
  }, [csvData.length]);

  // Navigate to first changed row when preview mode is activated
  useEffect(() => {
    if (previewMode && pendingChanges && csvData.length > 0) {
      let targetIndex = -1;

      if (pendingChanges.added?.length > 0) {
        // High priority: For additions, navigate to where they start (at the end of existing data)
        targetIndex = csvData.length;
      } else if (pendingChanges.updated?.length > 0) {
        const id = parseInt(pendingChanges.updated[0].row_id);
        targetIndex = csvData.findIndex(r => r._rowId === id);
      } else if (pendingChanges.deleted?.length > 0) {
        const id = parseInt(pendingChanges.deleted[0]);
        targetIndex = csvData.findIndex(r => r._rowId === id);
      }

      if (targetIndex !== -1) {
        const targetPage = Math.ceil((targetIndex + 1) / rowsPerPage);
        if (targetPage !== currentPage) {
          setCurrentPage(targetPage);
        }
      }
    }
  }, [previewMode, pendingChanges, csvData, rowsPerPage]);



  const tourSteps = [
    {
      target: 'body',
      content: (
        <div className="text-center py-2">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center shadow-lg">
            <Wand2 size={32} className="text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome to Srishti! ✨</h2>
          <p className="text-gray-600">Your AI-powered data creation and cleaning assistant. Let me show you around in just a few steps.</p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      styles: {
        tooltip: {
          width: '420px',
        }
      }
    },
    {
      target: '#upload-file-btn',
      content: (
        <div>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Upload size={18} className="text-blue-600" />
            Upload Your Data
          </h3>
          <p className="text-gray-600 text-sm">Already have data? Upload CSV, JSON, or JSONL files to get started. Cleaning works on tabular formats; conversational JSONL is <span className="font-medium text-indigo-600">generation-only</span> for now.</p>
          <div className="mt-3 flex gap-2 flex-wrap">
            <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">.csv</span>
            <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">.json</span>
            <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">.jsonl</span>
          </div>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '#clean-with-ai-btn',
      content: (
        <div>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Bot size={18} className="text-blue-600" />
            Generate & Refine
          </h3>
          {/* <p className="text-gray-600 text-sm">Describe the dataset you need and Srishti will generate it. Not happy with the result? Chat to refine the prompt — the AI will ask clarifying questions and regenerate.</p> */}
          <p className="text-gray-600 text-sm">Describe the dataset you need and Srishti will generate it. Not happy with the result? Chat to refine the prompt — the AI will ask follow-up questions to get it right.</p>
          <div className="mt-3 space-y-1">
            <div className="p-2 bg-gray-50 rounded text-xs text-gray-700">"Generate 50 rows of e-commerce product reviews"</div>
            <div className="p-2 bg-gray-50 rounded text-xs text-gray-700">"Make them more diverse in sentiment"</div>
          </div>
          <div className="mt-3">
            <p className="text-xs text-gray-500 font-medium mb-1.5">Supported output formats:</p>
            <div className="flex gap-2 flex-wrap">
              <span className="px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs font-medium text-blue-700">Structured (CSV / JSON)</span>
              <span className="px-2 py-1 bg-indigo-50 border border-indigo-200 rounded text-xs font-medium text-indigo-700">Conversational — <code>{'{"messages":[{"role":"user",...},{"role":"assistant",...}]}'}</code></span>
            </div>
          </div>
        </div>
      ),
      placement: 'left',
    },
    {
      target: '#ai-chat-input',
      content: (
        <div>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <MessageSquare size={18} className="text-blue-600" />
            Clean & Edit Your Data
          </h3>
          <p className="text-gray-600 text-sm">With structured data loaded, use the chat to clean, filter, or transform specific rows and columns — all in plain English.</p>
          <div className="mt-3 space-y-1">
            <div className="p-2 bg-gray-50 rounded text-xs text-gray-700">"Remove rows where email is missing"</div>
            <div className="p-2 bg-gray-50 rounded text-xs text-gray-700">"Capitalize all names in the customer column"</div>
          </div>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '#ai-context-search-toolbar',
      content: (
        <div>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Sparkles size={18} className="text-blue-600" />
            Context Files & Web Search
          </h3>
          <p className="text-gray-600 text-sm">Supercharge generation with extra context. Attach a <span className="font-medium">PDF or CSV</span> to guide the AI, or toggle <span className="font-medium">Web Search</span> to pull in live information.</p>
          <div className="mt-3 space-y-1">
            <div className="p-2 bg-gray-50 rounded text-xs text-gray-700 flex items-center gap-2">
              <span className="text-blue-500">📎</span> Attach a product spec PDF to generate relevant Q&amp;A
            </div>
            <div className="p-2 bg-gray-50 rounded text-xs text-gray-700 flex items-center gap-2">
              <span className="text-blue-500">🌐</span> Enable Web Search to generate data grounded in real facts
              <span className="ml-auto px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">Beta</span>
            </div>
          </div>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '#download-dropdown-wrapper',
      content: (
        <div>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <CheckCircle2 size={18} className="text-blue-600" />
            Export Your Data
          </h3>
          <p className="text-gray-600 text-sm">When you're satisfied, export in your preferred format — ready to use in your ML pipelines or projects!</p>
          <div className="mt-3 flex gap-2">
            <span className="px-2 py-1 bg-green-50 border border-green-200 rounded text-xs font-medium text-green-700">CSV</span>
            <span className="px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs font-medium text-blue-700">JSON</span>
            <span className="px-2 py-1 bg-yellow-50 border border-yellow-200 rounded text-xs font-medium text-yellow-700">HuggingFace</span>
          </div>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: 'body',
      content: (
        <div className="text-center py-2">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
            <CheckCircle2 size={32} className="text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">You're All Set! 🎉</h2>
          <p className="text-gray-600 mb-3">Generate new datasets, clean existing files, refine with context, or search the web — Srishti has you covered. Click Guide anytime to revisit this tour.</p>
        </div>
      ),
      placement: 'center',
      styles: {
        tooltip: {
          width: '420px',
        }
      }
    }
  ];

  const handleJoyrideCallback = (data) => {
    const { status, action, index, type } = data;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setTourStepIndex(nextStepIndex);

      // Open AI chat sidebar when reaching the Clean & Edit step (index 3)
      if (nextStepIndex === 3) {
        setIsChatOpen(true);
      }
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      setTourStepIndex(0);
      localStorage.setItem('srishti_tour_completed', 'true');
    }
  };

  const resetTour = () => {
    setTourStepIndex(0);
    setRunTour(true);
  };

  // Show notification helper
  const showNotification = (type, message) => {
    setNotification({ type, message, id: Date.now() });
    setTimeout(() => setNotification(null), 4000);
  };

  // Sample data for demonstration
  const sampleData = [
    { _rowId: 1, name: 'John Doe', age: 30, department: 'Engineering', salary: 75000, description: 'Senior Software Engineer with expertise in React and Node.js' },
    { _rowId: 2, name: 'Jane Smith', age: 28, department: 'Marketing', salary: 65000, description: 'Digital Marketing Specialist focusing on social media campaigns' },
    { _rowId: 3, name: 'Bob Johnson', age: 35, department: 'Engineering', salary: 80000, description: 'Lead Backend Developer specializing in microservices architecture' },
    { _rowId: 4, name: 'Alice Brown', age: 32, department: 'HR', salary: 70000, description: 'Human Resources Manager handling recruitment and employee relations' },
    { _rowId: 5, name: 'Charlie Wilson', age: 29, department: 'Marketing', salary: 68000, description: 'Content Marketing Manager creating engaging blog posts and articles' },
    { _rowId: 6, name: 'Diana Prince', age: 31, department: 'Engineering', salary: 85000, description: 'Frontend Developer with extensive experience in modern web technologies' },
    { _rowId: 7, name: 'Edward Norton', age: 33, department: 'Sales', salary: 72000, description: 'Account Executive managing key client relationships and business development' },
    { _rowId: 8, name: 'Fiona Green', age: 27, department: 'Design', salary: 63000, description: 'UX/UI Designer creating intuitive and visually appealing user interfaces' },
    { _rowId: 9, name: 'George Miller', age: 36, department: 'Engineering', salary: 90000, description: 'DevOps Engineer responsible for CI/CD pipelines and cloud infrastructure' },
    { _rowId: 10, name: 'Helen Davis', age: 30, department: 'Finance', salary: 75000, description: 'Financial Analyst conducting budget analysis and financial forecasting' },
    { _rowId: 11, name: 'Ian Thompson', age: 28, department: 'Marketing', salary: 66000, description: 'SEO Specialist optimizing website content for search engines' },
    { _rowId: 12, name: 'Julia Roberts', age: 34, department: 'HR', salary: 73000, description: 'Training and Development Coordinator organizing employee skill development programs' }
  ];

  const loadSampleData = () => {
    setCsvData(sampleData);
    setHeaders(Object.keys(sampleData[0]).filter(key => key !== '_rowId'));
    setChatMessages([{ type: 'system', content: 'Sample employee data loaded successfully!' }]);
    setCurrentPage(1);
    setManualChanges({});
    setActivityId(null);
    setLastUsedPrompt('');
    setAIEvaluation('');
    setIsAIEvalOpen(false);
  };

  const handleClearSession = () => {
    // If they had data, show feedback before clearing if not already shown
    if (csvData.length > 0 && !localStorage.getItem('srishti_feedback_shown')) {
      setShowFeedbackPopup(true);
      localStorage.setItem('srishti_feedback_shown', 'true');
    }

    setCsvData([]);
    setHeaders([]);
    setChatMessages([]);
    setCurrentInput('');
    setPendingChanges({ added: [], updated: [], deleted: [] });
    setPreviewMode(false);
    setManualChanges({});
    setIsChatOpen(false);
    setSearchResults([]);
    setGeneratePrompt('');
    setColumnNames('');
    setContextFile(null);
    setDuplicatesInfo(null);
    setIncludeSearch(false);
    setFormatType('tabular');
    setOriginalFileFormat(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (contextInputRef.current) contextInputRef.current.value = '';
    setActivityId(null);
    setLastUsedPrompt('');
    setAIEvaluation('');
    setIsAIEvalOpen(false);
    setIsGeneratedData(false);
    showNotification('success', 'Session cleared successfully. You can start fresh.');
  };

  // Helper function to convert streamed JSON string to array (from Home.js)
  const convertJsonStringToArray = (jsonString) => {
    const results = [];
    let depth = 0;
    let inString = false;
    let escapeNext = false;
    let startIndex = -1;

    for (let i = 0; i < jsonString.length; i++) {
      const char = jsonString[i];
      if (char === '"' && !escapeNext) {
        inString = !inString;
      }
      if (!inString) {
        if (char === '{') {
          if (depth === 0) startIndex = i;
          depth++;
        } else if (char === '}') {
          depth--;
          if (depth === 0 && startIndex !== -1) {
            let jsonStr = jsonString.substring(startIndex, i + 1);
            try {
              const cleanedJson = jsonStr.replace(/\\n/g, '');
              results.push(JSON.parse(cleanedJson));
            } catch (error) {
              try {
                results.push(JSON.parse(jsonStr));
              } catch (err) { }
            }
            startIndex = -1;
          }
        }
      }
      escapeNext = char === '\\' && !escapeNext;
    }
    return results;
  };

  const parseConversationalData = (jsonString) => {
    const results = [];
    let depth = 0;
    let inString = false;
    let escapeNext = false;
    let startIndex = -1;

    for (let i = 0; i < jsonString.length; i++) {
      const char = jsonString[i];
      if (char === '"' && !escapeNext) {
        inString = !inString;
      }
      if (!inString) {
        if (char === '{') {
          if (depth === 0) startIndex = i;
          depth++;
        } else if (char === '}') {
          depth--;
          if (depth === 0 && startIndex !== -1) {
            let jsonStr = jsonString.substring(startIndex, i + 1);
            try {
              const cleanedJson = jsonStr.replace(/\\n/g, '');
              results.push(JSON.parse(cleanedJson));
            } catch (error) {
              try {
                results.push(JSON.parse(jsonStr));
              } catch (err) { }
            }
            startIndex = -1;
          }
        }
      }
      escapeNext = char === '\\' && !escapeNext;
    }
    return results;
  };

  // Generate Data function - calls the same API as Home page
  const generateData = async (overridePrompt = null, isAppendMode = false, numRowsOverride = null) => {
    const isOverride = typeof overridePrompt === 'string';
    const basePrompt = isOverride ? overridePrompt : generatePrompt;
    const finalGenerateNumber = numRowsOverride || generateNumber;

    if (!basePrompt.trim() && !isRefiningPrompt) {
      showNotification('error', 'Please enter a prompt describing the data you want to generate.');
      return;
    }

    if (finalGenerateNumber < 1 || finalGenerateNumber > userPlan.maxRowsPerGeneration) {
      setUpgradeMessage(`Number of rows must be between 1 and ${userPlan.maxRowsPerGeneration} for your plan.`);
      setShowUpgradePopup(true);
      return;
    }

    // Check feature availability
    if (includeSearchRef.current && !userPlan.features.webSearch) {
      setUpgradeMessage('Web search context is available on Pro plans and above. Please upgrade to use this feature.');
      setShowUpgradePopup(true);
      return;
    }

    // Capture current state values into constants for the duration of this async call
    const capturedColumnNames = columnNames;
    const capturedIncludeSearch = includeSearch;
    const capturedGenerateNumber = generateNumber;

    setIsGenerating(true);
    setIsGeneratedData(true);
    setShowGenerateModal(false);

    // Staged progress handling
    setGenLoadingStep(0);
    if (genPreloadIntervalRef.current) clearInterval(genPreloadIntervalRef.current);
    genPreloadIntervalRef.current = setInterval(() => {
      setGenLoadingStep(prev => (prev >= 4 ? 4 : prev + 1));
    }, 1500);
    if (contextInputRef.current) {
      contextInputRef.current.value = '';
    }

    setSearchResults([]); // Reset search results
    if (isAppendMode) {
      setChatMessages(prev => [...prev, { type: 'system', content: 'Appending data... Please wait.' }]);
    } else {
      setChatMessages(prev => [...prev, { type: 'system', content: 'Generating data... Please wait.' }]);
    }

    try {
      const formData = new FormData();

      let finalPrompt = basePrompt;
      if (isOverride) {
        finalPrompt = basePrompt;
      } else if (isRefiningPrompt) {
        // The user suggested that the last conversation message is enough
        // instead of concatenating the entire transcript.
        const userMessages = chatMessages.filter(m => m.type === 'user');
        if (userMessages.length > 0) {
          finalPrompt = userMessages[userMessages.length - 1].content;
        } else {
          finalPrompt = generatePrompt;
        }
      }

      setLastUsedPrompt(finalPrompt);
      formData.append('prompt', finalPrompt);
      formData.append('usecase', 'custom');
      formData.append('number', finalGenerateNumber);
      formData.append('language', 'en');
      formData.append('purpose', '');
      formData.append('user_id', user ? user.uid : 'anonymous');
      formData.append('include_search_results', includeSearchRef.current);
      formData.append('temperature', 1);
      formData.append('model', 'llama-3.3-70b-versatile');
      formData.append('is_open_source', true);
      let finalColumnNames = capturedColumnNames;
      const currentHeaders = headersRef.current;
      if (isAppendMode && currentHeaders && currentHeaders.length > 0) {
        // If appending, instruct the AI to generate identical columns
        finalColumnNames = currentHeaders.filter(h => h !== "_rowId").join(", ");
      }
      formData.append('column_names', finalColumnNames);
      if (userSelectedFormat) {
        formData.append('format_type', userSelectedFormat);
      }

      if (contextFileRef.current) {
        formData.append('file', contextFileRef.current);
      }

      const apiHeaders = {};
      if (user && user.accessToken) {
        apiHeaders['Authorization'] = `Bearer ${user.accessToken}`;
      }

      const response = await fetch(`${API_URL}/api/generate-data`, {
        method: 'POST',
        body: formData,
        headers: apiHeaders,
      });

      // Handle 401 errors automatically
      // await handleResponseError(response);

      if (response.status === 400) {
        try {
          const errData = await response.json();
          const msg = errData?.datacreatoraimsg || 'Bad request. Please check your input and try again.';
          showNotification('error', msg);
          setChatMessages([{ type: 'system', content: `⚠️ ${msg}` }]);
        } catch {
          showNotification('error', 'Bad request. Please check your input and try again.');
        }
        return;
      } else if (!response.ok && response.status !== 401) {
        throw new Error('Some unknown error occured. Please try after some time.');
      } else if (response.status === 401) {
        showNotification('error', 'Session expired. Redirecting to sign-in...');
        window.location.href = '/signin';
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let allData = [];
      let counter = 0;
      let capturedSearchResults = [];
      let currentActivityId = null;
      let streamBuffer = '';

      // If appending, capture the existing length to adjust row IDs
      const baseDataAtStart = isAppendMode ? [...csvDataRef.current] : [];
      const startRowIdOffset = baseDataAtStart.length;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        streamBuffer += decoder.decode(value, { stream: true });
        let items_converted = convertJsonStringToArray(streamBuffer);

        if (items_converted.length === 0) {
          continue;
        }

        // Check for error message
        if ("datacreatoraimsg" in items_converted[0]) {
          showNotification('error', items_converted[0].datacreatoraimsg);
          setUpgradeMessage('');
          // setShowUpgradePopup(true);
          break;
        }

        // Check for search results or sources
        const searchResItem = items_converted.find(item => item && (item.search_results || item.sources));
        if (searchResItem) {
          capturedSearchResults = searchResItem.search_results || searchResItem.sources;
          setSearchResults(capturedSearchResults);
          // Filter out search results from data rows before mapping
          items_converted = items_converted.filter(item => !item.search_results && !item.sources);
        }

        if (items_converted.length === 0) continue;

        // Check for format_type
        const formatTypeItem = items_converted.find(item => item && item.format_type);
        if (formatTypeItem) {
          setFormatType(formatTypeItem.format_type);
        }

        const currentActivityItem = items_converted.find(item => item && item.activity_id);
        if (currentActivityItem) {
          currentActivityId = currentActivityItem.activity_id;
          setActivityId(currentActivityId);
        }

        // Filter out metadata elements
        items_converted = items_converted.filter(item => !item.format_type && !item.activity_id);

        if (items_converted.length === 0) continue;

        // --- Stream Chat Format Parsing ---
        const isStreamChatFormat = items_converted.length > 0 &&
          ("messages" in items_converted[0]);

        if (isStreamChatFormat || formatTypeItem?.format_type?.toLowerCase().includes('conversational') || formatTypeItem?.format_type?.toLowerCase() === 'chat_array') {
          // Re-parse with the robust State Machine approach for conversational data
          // since it can throw off the regex via nested strings.
          let conversational_items = parseConversationalData(streamBuffer);

          // Re-filter out generic metadata so we only process rows
          items_converted = conversational_items.filter(item => !item.datacreatoraimsg && !item.search_results && !item.sources && !item.format_type);

          setOriginalFileFormat('jsonl-chat');

          items_converted = items_converted.map(item => {
            let msgs = item.messages || item;
            if (typeof msgs === 'string') {
              try { msgs = JSON.parse(msgs); } catch (e) { }
            }
            if (msgs && typeof msgs === 'object' && msgs.messages) msgs = msgs.messages;

            if (Array.isArray(msgs) && msgs.some(m => m && m.role)) {
              const row = { ...item };
              delete row.messages;
              const roleCounts = {};
              msgs.forEach(msg => {
                if (!msg) return;
                const role = (msg.role || 'unknown').toLowerCase();
                roleCounts[role] = (roleCounts[role] || 0) + 1;
                const columnName = `${role}_${roleCounts[role]}`;
                row[columnName] = msg.content || '';
              });
              return row;
            }
            return item;
          }).filter(item => {
            // Drop rows where every conversation column is empty/blank
            // (blank server response or all-null content fields)
            const convKeys = Object.keys(item).filter(k =>
              k !== '_rowId' && /^(user|assistant|system|tool)_\d+$/i.test(k)
            );
            if (convKeys.length === 0) return false; // malformed or empty chat array -> drop it
            return convKeys.some(k => item[k] != null && String(item[k]).trim().length > 0);
          });

          if (counter > 0) {
            setHeaders(prevHeaders => {
              const newSet = new Set(prevHeaders);
              items_converted.forEach(item => {
                Object.keys(item).forEach(k => {
                  if (k !== '_rowId') newSet.add(k);
                });
              });
              return Array.from(newSet);
            });
          }
        }
        // --- End Chat Format Parsing ---

        // If this is the first chunk, set up headers and stop staged progress
        if (counter === 0 && items_converted.length > 0) {
          if (genPreloadIntervalRef.current) {
            clearInterval(genPreloadIntervalRef.current);
            genPreloadIntervalRef.current = null;
          }
          setGenLoadingStep(0);
          const headerSet = new Set();
          items_converted.forEach(item => {
            Object.keys(item).forEach(k => headerSet.add(k));
          });
          setHeaders(Array.from(headerSet));
        }

        counter += 1;

        allData = removeAbsoluteDuplicates(items_converted).map((item, index) => ({
          ...item,
          _rowId: startRowIdOffset + index + 1
        }));

        // Update UI with streaming progress - show data immediately in grid
        if (isAppendMode) {
          setCsvData([...baseDataAtStart, ...allData]);
        } else {
          setCsvData([...allData]);
        }
        setCurrentPage(1);
        setChatMessages(prev => {
          // Replace the last "Generating..." system message with the progress update if it exists
          if (prev.length > 0 && prev[prev.length - 1].type === 'system' && prev[prev.length - 1].content.startsWith('Generating')) {
            return [...prev.slice(0, -1), {
              type: 'system',
              content: `Generating data... ${allData.length} rows received.`
            }];
          }
          return [...prev, {
            type: 'system',
            content: `Generating data... ${allData.length} rows received.`
          }];
        });
      }

      // Remove duplicates from the new batch
      const uniqueNewData = removeAbsoluteDuplicates(allData);

      let finalData;
      if (isAppendMode) {
        // If appending, combine existing and new, then assign proper row IDs
        const combined = [...baseDataAtStart, ...uniqueNewData];
        finalData = combined.map((item, index) => ({
          ...item,
          _rowId: index + 1
        }));
      } else {
        finalData = uniqueNewData;
      }

      setCsvData(finalData);
      setCurrentPage(1);
      setManualChanges({});

      if (isAppendMode) {
        showNotification('success', `Data appended successfully! Added ${uniqueNewData.length} new rows. Total rows: ${finalData.length}`);
      } else {
        showNotification('success', `Data generation complete! ${finalData.length} rows generated. (Limit: 500 per request. You can continue adding more!)`);
      }

      const actions = [];
      if (capturedSearchResults.length > 0) {
        actions.push({
          label: 'View Sources',
          onClick: () => setShowSourcesModal(true)
        });
        setShowSourcesModal(true); // Auto-open sources popup
      }

      // Interactive Mode: Post-Generation Analysis
      if (isInteractiveGeneration) {
        // Mock analysis of topics
        const promptWords = generatePrompt.toLowerCase().split(' ').filter(w => w.length > 4);
        const covered = promptWords.slice(0, Math.min(3, promptWords.length));
        const uncovered = ["edge_cases", "anomalies", "rare_events"]; // Mock unimplemented topics

        setChatMessages(prev => [...prev, {
          type: 'assistant',
          content: `Data generation complete! ${uniqueNewData.length} rows generated.\n\n**Topics Covered:**\n${covered.map(t => `- ${t}`).join('\n')}\n\n**Potential Uncovered Topics:**\n${uncovered.map(t => `- ${t}`).join('\n')}\n\nWould you like to generate more data for the uncovered topics?`,
          actions: [
            {
              label: 'Generate More Variations', onClick: () => {
                setGeneratePrompt(prev => prev + " Include rare events and edge cases.");
                generateData(); // Recursive call logic would ideally require state update before call, but for simplicity re-trigger
              }
            },
            { label: 'I\'m Done', onClick: () => setIsInteractiveGeneration(false) },
            ...actions // Include View Sources if available
          ]
        }]);
      } else {
        const improveActions = [
          ...actions,
          {
            label: '✨ Improve with AI',
            onClick: () => {
              setIsChatOpen(true);
            }
          }
        ];
        setChatMessages(prev => [...prev, {
          type: 'assistant',
          content: isAppendMode
            ? `✅ Successfully appended ${uniqueNewData.length} new rows! Your dataset now has ${finalData.length} rows.`
            : `✅ ${finalData.length} rows generated successfully! (Note: 500 rows per request. You can keep adding more data points below!)\n\n⚠️ **Important:** These instructions apply to this specific dataset. If you want to generate different kinds of rows or start a new task, you should **Clear All** and start afresh.`,
          actions: isAppendMode ? [] : [
          ]
        }]);
      }
      // Reset refinement state so future chats are treated as cleaning commands
      setIsRefiningPrompt(false);

      // Reset download tracking for new generation - allow feedback popup on next download
      localStorage.removeItem('srishti_has_downloaded');

      // Trigger Auto Quality Evaluation (Non-blocking)
      if (currentActivityId) {
        triggerAutoQualityEval(currentActivityId, finalPrompt, finalData);
      }

      // Finally, clear the form inputs so the next time the modal is opened it's fresh
      setGeneratePrompt('');
      setGenerateNumber(100);
      setColumnNames('');
      setContextFile(null);
      setIncludeSearch(false);
      setActivityId(null);

    } catch (error) {
      console.error('Error generating data:', error);
      // alert(error);
      showNotification('error', 'Failed to generate data. Please try again.');
      setChatMessages(prev => [...prev, { type: 'system', content: 'Error generating data. Please try again.' }]);
    } finally {
      setIsGenerating(false);
    }
  };

  // Custom Order handler — calls /api/order-for-later
  const handleCustomOrder = async (prompt, rowNumber = null, isPaidScale = false) => {
    if (!user) {
      showNotification('error', 'Please sign in to place a custom order.');
      setShowAuthModal(true);
      return;
    }
    if (!prompt || !prompt.trim()) {
      showNotification('error', 'Please enter a prompt before placing a custom order.');
      return;
    }
    try {
      // Build full history text for the background processing team
      const historyText = chatMessages
        .filter(m => m.type === 'user' || m.type === 'assistant')
        .map(m => `[${m.type.toUpperCase()}]: ${m.content}`)
        .join('\n\n');

      const formData = new FormData();
      // Include history in prompt so backend team has full context
      const fullPrompt = `${prompt}\n\n--- FULL CONVERSATION HISTORY ---\n${historyText}`;

      formData.append('prompt', fullPrompt);
      formData.append('updatedPrompt', fullPrompt);
      formData.append('usecase', 'custom');
      formData.append('number', rowNumber || generateNumber || 100);
      formData.append('language', 'en');
      formData.append('purpose', '');
      formData.append('user_id', user.uid);
      formData.append('include_search_results', includeSearchRef.current); // Send web search status
      formData.append('temperature', 1);
      formData.append('model', 'llama-3.3-70b-versatile');
      formData.append('is_open_source', true);
      formData.append('column_names', columnNames || '');

      // Include contextual files if any were uploaded
      if (contextFileRef.current) {
        formData.append('file', contextFileRef.current);
      }

      const response = await fetch(`${API_URL}/api/order-for-later`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user.accessToken}` },
        body: formData
      });

      if (response.status === 400) {
        try {
          const errData = await response.json();
          const msg = errData?.datacreatoraimsg || 'Bad request. Please check your input and try again.';
          showNotification('error', msg);
        } catch {
          showNotification('error', 'Bad request. Please check your input and try again.');
        }
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();

      if (isPaidScale) {
        // Clear indication that payment was successful and processing started
        showNotification('success', 'Payment Successful! Our team is now generating your 5,000 row dataset.');

        setChatMessages(prev => [...prev, {
          type: 'assistant',
          content: `✅ **Payment Successful!**\n\nYour order for 5,000 rows has been received and processing has started. You will receive an email at **${user.email}** once your dataset is ready.\n\n*Note: You will be automatically refunded within 48 hours if for some reason we cannot process this specific request.*`
        }]);
      } else {
        showNotification('success', data.datacreatoraimsg || 'Custom order placed! We will get back to you soon.');
        setChatMessages(prev => [...prev, {
          type: 'system',
          content: '📬 Custom order submitted! Our team will review your request and get back to you with a quote.'
        }]);
      }
    } catch (error) {
      console.error('Error placing custom order:', error);
      showNotification('error', 'Failed to place custom order. Please try again.');
    }
  };

  const handleContextFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const limitMB = userPlan.features.fileUploadLimitMB;
      if (file.size > limitMB * 1024 * 1024) {
        setUpgradeMessage(`File size exceeds ${limitMB}MB limit for your plan.`);
        setShowUpgradePopup(true);
        return;
      }
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');

      if (!isPdf && !isCsv) {
        showNotification('error', 'Please upload a valid PDF or CSV file.');
        return;
      }
      setContextFile(file);
      setIncludeSearch(false); // context file and web search are mutually exclusive
      showNotification('success', `Context file "${file.name}" attached.`);

      if (isCsv) {
        try {
          const headers = await readCSVHeader(file);
          setColumnNames(headers.join(', '));
        } catch (error) {
          console.error("Error reading CSV headers:", error);
          showNotification('error', 'Failed to read CSV headers.');
        }
      }
    }
  };

  // Helper function to get the old value for tooltips
  const getOldValue = (row, column) => {
    if (row._rowId == null) return null;
    const rowIdStr = String(row._rowId);
    const updateChange = pendingChanges.updated.find(u =>
      u.row_id === rowIdStr && u.changes[column]
    );
    if (!updateChange) return null;
    const changeVal = updateChange.changes[column];
    if (Array.isArray(changeVal)) return changeVal[0];
    if (changeVal && typeof changeVal === 'object') return changeVal.old ?? null;
    return null;
  };

  // New helper function to handle cell editing
  const startEditing = (rowId, column, currentValue) => {
    setEditingCell({ rowId, column });
    setEditValue(currentValue || '');
  };

  // Helper function to determine if content should use textarea
  const shouldUseTextarea = (value) => {
    if (!value) return false;
    const stringValue = String(value);
    // Use textarea for long text (>50 chars) or text with line breaks
    return stringValue.length > 50 || stringValue.includes('\n') || stringValue.includes('\r');
  };

  // Helper function to determine if content is likely numeric
  const isNumericContent = (value) => {
    if (!value) return false;
    const stringValue = String(value).trim();
    // Check if it's a number (including decimals, negatives, and common formats)
    return /^-?\d*\.?\d+$/.test(stringValue) || /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(stringValue);
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = () => {
    if (!editingCell) return;

    const { rowId, column } = editingCell;
    const originalValue = csvData.find(row => row._rowId === rowId)[column];

    // Only save if value actually changed
    if (editValue !== originalValue) {
      // Update the csvData directly
      setCsvData(prev => prev.map(row =>
        row._rowId === rowId
          ? { ...row, [column]: editValue }
          : row
      ));

      // Track manual changes for visual indication
      setManualChanges(prev => ({
        ...prev,
        [`${rowId}-${column}`]: {
          oldValue: originalValue,
          newValue: editValue
        }
      }));
    }

    cancelEditing();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  // Improved CSV parsing with proper quote and newline handling
  const parseCSV = (csvText) => {
    const rows = [];
    let currentRow = [];
    let currentField = '';
    let inQuotes = false;

    // Normalize line endings to simple \n to handle \r\n
    const text = csvText.replace(/\r\n/g, '\n');

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Double quote inside quotes is an escaped quote
          currentField += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // Field delimiter
        currentRow.push(currentField);
        currentField = '';
      } else if (char === '\n' && !inQuotes) {
        // Row delimiter
        currentRow.push(currentField);
        if (currentRow.length > 0 || currentField.length > 0) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
      } else {
        // Regular character
        currentField += char;
      }
    }

    // Handle the last field/row
    if (currentField.length > 0 || currentRow.length > 0) {
      currentRow.push(currentField);
      rows.push(currentRow);
    }

    // Filter out completely empty rows (e.g. initial blank lines)
    const validRows = rows.filter(r => r.length > 0 && r.some(cell => cell && cell.trim().length > 0));

    if (validRows.length === 0) return { headers: [], data: [] };

    // Header is the first valid row
    const rawHeaders = validRows[0].map(h => h.trim());
    console.log('[CSV Parse] Raw headers:', rawHeaders);

    // Identify valid columns (non-empty headers)
    const validIndices = rawHeaders
      .map((h, i) => h.length > 0 ? i : -1)
      .filter(i => i !== -1);

    const headers = validIndices.map(i => rawHeaders[i]);
    console.log('[CSV Parse] Clean headers:', headers);

    // Build data objects using valid indices
    const data = validRows.slice(1).map((rowFields, index) => {
      const row = { _rowId: index + 1 };
      headers.forEach((header, i) => {
        // Map clean header index back to original column index
        const originalColIndex = validIndices[i];
        row[header] = rowFields[originalColIndex] || '';
      });
      return row;
    });

    return { headers, data };
  };



  const removeAbsoluteDuplicates = (objects) => {
    const seen = new Set();
    return objects.filter(obj => {
      const key = Object.entries(obj)
        .filter(([k, v]) => !['_rowId', 'row_id', 'id'].includes(k))
        .map(([k, v]) => `${k}:${String(v).toLowerCase().trim()}`)
        .sort() // ensures different key orders are treated the same
        .join('|');

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // Helper to flatten nested JSON objects
  const flattenObject = (obj, prefix = '', result = {}) => {
    Object.keys(obj).forEach(key => {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        flattenObject(obj[key], newKey, result);
      } else if (Array.isArray(obj[key])) {
        result[newKey] = JSON.stringify(obj[key]);
      } else {
        result[newKey] = obj[key];
      }
    });
    return result;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsGeneratedData(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    const limitMB = userPlan.features.fileUploadLimitMB;
    if (file.size > limitMB * 1024 * 1024) {
      showNotification('error', `File size exceeds ${limitMB}MB limit for your plan.`);
      return;
    }

    // Lazy load: read only the first 10MB of the file for the preview
    // We only process the first 5000 rows anyway.
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
    const isLargeFile = file.size > CHUNK_SIZE;
    const blobToRead = isLargeFile ? file.slice(0, CHUNK_SIZE) : file;

    const reader = new FileReader();
    reader.onload = (e) => {
      let content = e.target.result;

      // If we sliced, avoid broken syntaxes at the end
      if (isLargeFile) {
        if (file.name.toLowerCase().endsWith('.jsonl') || file.name.toLowerCase().endsWith('.csv')) {
          // Remove the last incomplete line
          const lastNewlineIndex = content.lastIndexOf('\n');
          if (lastNewlineIndex > 0) {
            content = content.substring(0, lastNewlineIndex);
          }
        } else if (file.name.toLowerCase().endsWith('.json')) {
          // Try to aggressively fix broken JSON arrays
          const lastBracket = content.lastIndexOf('}');
          if (lastBracket > 0) {
            content = content.substring(0, lastBracket + 1) + ']';
          }
        }
      }

      let parsedData = [];
      let parsedHeaders = [];

      try {
        if (file.name.toLowerCase().endsWith('.csv')) {
          setOriginalFileFormat('csv');
          setFormatType('tabular');
          const { headers, data } = parseCSV(content);
          parsedHeaders = headers;
          parsedData = data;
        } else if (file.name.toLowerCase().endsWith('.json') || file.name.toLowerCase().endsWith('.jsonl')) {
          let dataArray;

          if (file.name.toLowerCase().endsWith('.jsonl')) {
            dataArray = content.split('\n')
              .filter(line => line.trim())
              .map(line => {
                try {
                  return JSON.parse(line);
                } catch (e) {
                  return null; // Handle partial line exceptions smoothly if trailing logic missed one
                }
              })
              .filter(item => item !== null);
          } else {
            const jsonContent = JSON.parse(content);
            dataArray = Array.isArray(jsonContent) ? jsonContent : [jsonContent];
          }

          const isChatFormat = dataArray.length > 0 &&
            dataArray[0].messages &&
            Array.isArray(dataArray[0].messages) &&
            dataArray[0].messages.some(msg =>
              msg.role && ['user', 'assistant', 'system'].includes(msg.role.toLowerCase())
            );

          if (isChatFormat) {
            setFormatType('conversational');
            setOriginalFileFormat('jsonl-chat');
            const roleCountMap = new Map();
            dataArray.forEach(item => {
              if (item.messages) {
                const roleCounts = {};
                item.messages.forEach(msg => {
                  const role = (msg.role || 'unknown').toLowerCase();
                  roleCounts[role] = (roleCounts[role] || 0) + 1;
                });
                Object.entries(roleCounts).forEach(([role, count]) => {
                  roleCountMap.set(role, Math.max(roleCountMap.get(role) || 0, count));
                });
              }
            });

            const turnHeaders = [];
            dataArray.forEach(item => {
              if (item.messages) {
                item.messages.forEach((msg, idx) => {
                  const role = (msg.role || 'unknown').toLowerCase();
                  const turnNumber = item.messages.slice(0, idx + 1).filter(m =>
                    (m.role || 'unknown').toLowerCase() === role
                  ).length;
                  const columnName = `${role}_${turnNumber}`;
                  if (!turnHeaders.includes(columnName)) {
                    turnHeaders.push(columnName);
                  }
                });
              }
            });
            parsedHeaders = turnHeaders;

            parsedData = dataArray.map((item, index) => {
              const row = { _rowId: index + 1 };
              if (item.messages) {
                const roleCounts = {};
                item.messages.forEach((msg) => {
                  const role = (msg.role || 'unknown').toLowerCase();
                  roleCounts[role] = (roleCounts[role] || 0) + 1;
                  const columnName = `${role}_${roleCounts[role]}`;
                  row[columnName] = msg.content || '';
                });
              }
              turnHeaders.forEach(h => {
                if (row[h] === undefined) row[h] = '';
              });
              return row;
            });
          } else {
            if (file.name.toLowerCase().endsWith('.jsonl')) {
              setOriginalFileFormat('jsonl');
              setFormatType('tabular');
            } else {
              setOriginalFileFormat('json');
              setFormatType('tabular');
            }

            const flattenedData = dataArray.map((item, index) => {
              const flat = flattenObject(item);
              return { _rowId: index + 1, ...flat };
            });

            const headerSet = new Set();
            flattenedData.forEach(row => {
              Object.keys(row).forEach(k => {
                if (k !== '_rowId') headerSet.add(k);
              });
            });

            parsedHeaders = Array.from(headerSet);
            parsedData = flattenedData.map(row => {
              const normalizedRow = { ...row };
              parsedHeaders.forEach(h => {
                if (normalizedRow[h] === undefined) normalizedRow[h] = '';
              });
              return normalizedRow;
            });
          }
        } else {
          setChatMessages([{ type: 'system', content: 'Unsupported file type. Please upload a .csv, .json, or .jsonl file.' }]);
          return;
        }

        const limitedData = parsedData.slice(0, 5000);
        const wasLimited = parsedData.length > 5000 || isLargeFile;

        const uniqueData = removeAbsoluteDuplicates(limitedData);
        const originalCount = limitedData.length;
        const uniqueCount = uniqueData.length;
        const duplicatesRemoved = originalCount - uniqueCount;

        if (duplicatesRemoved > 0) {
          const seen = new Set();
          const duplicateRows = [];
          limitedData.forEach(obj => {
            const key = Object.entries(obj)
              .filter(([k, v]) => !['_rowId', 'row_id', 'id'].includes(k))
              .map(([k, v]) => `${k}:${String(v).toLowerCase().trim()}`)
              .sort()
              .join('|');

            if (seen.has(key)) {
              duplicateRows.push(obj);
            } else {
              seen.add(key);
            }
          });

          setDuplicatesInfo({
            originalData: limitedData,
            duplicatesCount: duplicatesRemoved,
            duplicateRows: duplicateRows
          });
        } else {
          setDuplicatesInfo(null);
        }

        setCsvData(uniqueData);
        setHeaders(parsedHeaders);
        setManualChanges({});
        setActivityId(null);
        setLastUsedPrompt('');
        setAIEvaluation('');
        setIsAIEvalOpen(false);

        const fileType = file.name.toLowerCase().endsWith('.jsonl') ? 'JSONL' :
          file.name.toLowerCase().endsWith('.json') ? 'JSON' : 'CSV';
        let message = `${fileType} file loaded successfully! ${uniqueData.length} rows found.`;
        if (wasLimited) {
          message = `${fileType} file loaded and sliced for preview. Cleaning is limited to 5000 rows for now.`;
        }
        if (duplicatesRemoved > 0) {
          message += ` ${duplicatesRemoved} duplicate(s) removed.`;
        }
        setChatMessages([{ type: 'system', content: message }]);
        setCurrentPage(1); // Ensure pagination is reset to 1

        // Reset download tracking for new upload - allow feedback popup on next download
        localStorage.removeItem('srishti_has_downloaded');

      } catch (error) {
        console.error("File parse error:", error);
        setChatMessages([{ type: 'system', content: `Error parsing file: ${error.message}` }]);
      }
    };
    reader.readAsText(blobToRead);
  };

  // Modified submitToAPI function
  const submitToAPI = async (instructions, data, onProgress = null) => {
    if (!user) {
      showNotification('error', 'Please sign in to clean data.');
      setShowAuthModal(true);
      return;
    }
    try {
      const payload = {
        "prompt": instructions,
        "dataToClean": data
      };

      const response = await fetch(`${API_URL_AGENT}/api/clean-dataset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
          // 'Authorization': `Bearer ${user.accessToken}`
        },
        body: JSON.stringify(payload)
      });

      // Handle 401 errors automatically
      await handleResponseError(response);

      if (!response.ok) {
        if (response.status === 400) {
          try {
            const errBody = await response.json();
            if (errBody && errBody.datacreatoraimsg) {
              throw new Error(errBody.datacreatoraimsg);
            }
          } catch (parseErr) {
            if (parseErr.message !== `HTTP error! status: ${response.status}`) throw parseErr;
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if response is streaming (Server-Sent Events)
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('text/event-stream')) {
        // Handle streaming response (rewrite mode)
        return await handleStreamingResponse(response, onProgress);
      } else {
        // Handle regular JSON response (codegen mode)
        const result = await response.json();
        return result;
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      throw error;
    }
  };

  // Handle streaming response
  const handleStreamingResponse = async (response, onProgress) => {
    return new Promise((resolve, reject) => {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult = null;

      function readStream() {
        reader.read().then(({ done, value }) => {
          if (done) {
            if (finalResult) {
              resolve(finalResult);
            } else {
              reject(new Error('Stream ended without final result'));
            }
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // Keep incomplete line in buffer

          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) continue;

            // Intercept format_type payload that might not have 'data: ' prefix
            if (trimmedLine.startsWith('{') && trimmedLine.endsWith('}')) {
              try {
                const data = JSON.parse(trimmedLine);
                if (data.format_type) {
                  setFormatType(data.format_type);
                  continue;
                }
              } catch (e) {
                // Ignore plain parsing errors, might be partial stream
              }
            }

            if (trimmedLine.startsWith('data: ')) {
              try {
                const jsonStr = line.slice(6); // Remove 'data: ' prefix
                if (jsonStr.trim()) {
                  const data = JSON.parse(jsonStr);

                  if (data.complete) {
                    // Final result
                    finalResult = data;
                  } else {
                    // Progress update
                    if (onProgress) {
                      onProgress(data);
                    }
                  }
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e, 'Raw line:', line);
              }
            }
          }
          readStream();
        }).catch(reject);
      }

      readStream();
    });
  };

  // Keep sendToBackend simple
  const sendToBackend = async (prompt, data, onProgress = null) => {
    return await submitToAPI(prompt, data, onProgress);
  };

  // Audio recording handler using AudioService
  const audioHandler = createAudioRecordingHandler({
    mediaRecorderRef,
    audioChunksRef,
    isRecording,
    setIsRecording,
    setIsTranscribing,
    user,
    onTranscriptionComplete: (transcribedText) => {
      // Append transcribed text to current input
      setCurrentInput(prev => {
        const separator = prev && !prev.endsWith(' ') ? ' ' : '';
        return prev + separator + transcribedText;
      });
    },
    showNotification
  });

  const toggleRecording = audioHandler.toggleRecording;

  const handleRefinePrompt = async () => {
    setIsRefiningPrompt(true);
    setShowGenerateModal(false);
    setIsChatOpen(true);

    // Add the initial prompt to chat history so it's included in future context
    const initialUserMsg = {
      type: 'user',
      content: generatePrompt
    };

    setChatMessages([initialUserMsg]);
    setIsProcessing(true);

    try {
      // Use the uniform API call
      // History is empty for the first turn, input is the prompt
      const response = await callClarificationAPI([initialUserMsg], generatePrompt, user?.accessToken);

      if (response.success) {
        const assistantReply = response.result?.assistant_message || "Ready to generate data.";
        const isInvalid = assistantReply.includes('[INVALID]');
        const isReady = response.result?.ready || false;

        if (isInvalid) {
          setChatMessages(prev => [...prev, {
            type: 'assistant',
            content: '🚫 This is not a legal request. Generating illicit or harmful data is strictly forbidden.',
            isInvalidRequest: true
          }]);
        } else {
          let actions = [
            {
              label: 'Generate Now',
              onClick: () => {
                generateData(generatePrompt);
              }
            },
            {
              label: 'Custom Order',
              isPaid: true,
              onClick: () => {
                handleCustomOrder(generatePrompt);
              }
            }
          ];

          setChatMessages(prev => [...prev, {
            type: 'assistant',
            content: assistantReply,
            actions: actions
          }]);
        }
      } else {
        setChatMessages(prev => [...prev, {
          type: 'assistant',
          content: "I'm ready to help refine your prompt. What specific details would you like to add?"
        }]);
      }
    } catch (error) {
      console.error("Refinement error:", error);
      setChatMessages(prev => [...prev, { type: 'assistant', content: "Ready to refine. What would you like to improve?" }]);
    } finally {
      setIsProcessing(false);
    }
  };

  /*
  const handleRegenerateClick = () => {
    setIsChatOpen(true);
    setChatMessages(prev => [...prev, {
      type: 'assistant',
      content: "Do you want to re-generate this dataset entirely? Or just tell us what should change?",
      actions: [
        {
          label: '🔄 Regenerate Entirely',
          onClick: () => {
            generateData(lastUsedPrompt);
          }
        },
        {
          label: '✏️ Suggest Changes',
          onClick: () => {
            setCurrentInput("I'd like to change...");
            // Set refinement mode so the next message is treated as feedback
            setIsRefiningPrompt(true);
            const inputElement = document.getElementById('ai-chat-input');
            if (inputElement) {
              inputElement.focus();
            }
          }
        }
      ]
    }]);
  };
  */

  const handleSendMessage = async () => {
    if (!currentInput.trim()) return;

    // --- REFINEMENT MODE OR GENERATION LOGIC ---
    if (isRefiningPrompt || csvData.length === 0) {
      const userMessage = { type: 'user', content: currentInput };

      // Optimistically update UI
      setChatMessages(prev => [...prev, userMessage]);
      setCurrentInput('');
      setIsProcessing(true);

      try {
        // Send FULL context + new input to the specific cleanup endpoint
        // Filter out system messages if backend doesn't support them, or keep them if it does.
        // Usually, 'system' messages in UI like "Started Refinement..." aren't needed by LLM, 
        // but the initial prompt might be. Let's send relevant history.

        const relevantHistory = chatMessages.map(m => ({
          role: m.type === 'assistant' ? 'assistant' : 'user', // simple mapping
          content: m.content
        }));

        const response = await callClarificationAPI(relevantHistory, currentInput, user?.accessToken);

        if (response.success) {
          const assistantReply = response.result?.assistant_message || "Could you provide more details?";
          const isInvalid = assistantReply.includes('[INVALID]');
          const isReady = response.result?.ready || false;

          if (isInvalid) {
            setChatMessages(prev => [...prev, {
              type: 'assistant',
              content: '🚫 This is not a legal request. Generating illicit or harmful data is strictly forbidden.',
              isInvalidRequest: true
            }]);
          } else {
            let actions = [
              {
                label: 'Generate Now',
                onClick: () => {
                  const userMessages = relevantHistory.filter(m => m.role === 'user').map(m => m.content).join('\n');
                  const combinedPrompt = userMessages ? `${userMessages}\n${currentInput}` : currentInput;
                  generateData(combinedPrompt);
                }
              },
              {
                label: 'Custom Order',
                isPaid: true,
                onClick: () => {
                  const userMessages = relevantHistory.filter(m => m.role === 'user').map(m => m.content).join('\n');
                  const combinedPrompt = userMessages ? `${userMessages}\n${currentInput}` : currentInput;
                  handleCustomOrder(combinedPrompt);
                }
              }
            ];

            setChatMessages(prev => [...prev, {
              type: 'assistant',
              content: assistantReply,
              actions: actions
            }]);
          }
        } else {
          setChatMessages(prev => [...prev, { type: 'assistant', content: "Could not get a response. Please try generating or provide more details." }]);
        }

      } catch (error) {
        console.error("Refinement chat error:", error);
        setChatMessages(prev => [...prev, { type: 'system', content: "Error communicating with AI assistant." }]);
      } finally {
        setIsProcessing(false);
      }
      return;
    }
    // -----------------------------

    // if (csvData.length === 0) {
    //   setChatMessages(prev => [...prev,
    //   { type: 'user', content: currentInput },
    //   { type: 'assistant', content: 'Please load CSV data first before making changes.' }
    //   ]);
    //   setCurrentInput('');
    //   return;
    // }

    const userMessage = { type: 'user', content: currentInput };
    setChatMessages(prev => [...prev, userMessage]);
    setIsProcessing(true);
    setCurrentInput('');

    const processingMessageId = Date.now();
    setChatMessages(prev => [...prev, {
      id: processingMessageId,
      type: 'assistant',
      content: 'Processing your request...',
      isStreaming: true
    }]);

    // Reset streaming state
    streamingStateRef.current = { addedCount: 0, updatedCount: 0, deletedCount: 0 };

    try {
      // Build payload - preserve existing _rowId if it exists, otherwise assign based on index
      const csvDataWithIds = csvData.map((row, index) => {
        // If row already has _rowId, keep it; otherwise assign index + 1
        const rowId = row._rowId !== undefined ? row._rowId : (index + 1);
        return {
          ...row,
          _rowId: String(rowId) // Ensure it's a string for API consistency
        };
      });

      console.log('[Phase] Sending data to API with row IDs:',
        csvDataWithIds.slice(0, 3).map(r => ({ _rowId: r._rowId })));

      setChatMessages(prev => prev.map(msg =>
        msg.id === processingMessageId
          ? { ...msg, content: `Processing ${csvDataWithIds.length} rows...` }
          : msg
      ));

      // Progress callback for streaming updates
      const onProgress = (progressData) => {
        const { progress, cumulative_changes } = progressData;

        let progressMessage = 'Processing...';

        if (progress) {
          const { rows_processed, total_rows } = progress;
          const percentage = Math.round((rows_processed / total_rows) * 100) || 0;
          progressMessage = `Processing... ${percentage}% completed (${rows_processed}/${total_rows} rows)`;
        }

        if (cumulative_changes) {
          // Calculate delta based on what user has already processed (accepted/rejected)
          const addedOffset = streamingStateRef.current.addedCount;
          const updatedOffset = streamingStateRef.current.updatedCount;
          const deletedOffset = streamingStateRef.current.deletedCount;

          const deltaChanges = {
            ...cumulative_changes,
            added: (cumulative_changes.added || []).slice(addedOffset),
            updated: (cumulative_changes.updated || []).slice(updatedOffset),
            deleted: (cumulative_changes.deleted || []).slice(deletedOffset)
          };

          const totalChanges = (deltaChanges.added?.length || 0) +
            (deltaChanges.updated?.length || 0) +
            (deltaChanges.deleted?.length || 0);

          if (totalChanges > 0) {
            progressMessage += ` - ${totalChanges} new changes detected`;
            // console.log('[Phase] Delta changes detected:', deltaChanges);
            setPendingChanges(deltaChanges);
            setPreviewMode(true);
          }
        }

        setChatMessages(prev => prev.map(msg =>
          msg.id === processingMessageId
            ? { ...msg, content: progressMessage, isStreaming: true }
            : msg
        ));
      };

      // Call backend with progress callback
      const jsonResponse = await sendToBackend(currentInput, csvDataWithIds, onProgress);

      // Check for backend-provided AI helper messages or restrictions (e.g. scale-up requirement)
      if (jsonResponse && jsonResponse.datacreatoraimsg) {
        setChatMessages(prev => prev.map(msg =>
          msg.id === processingMessageId
            ? {
              type: 'assistant',
              content: `✅ Data generated successfully! (Max 500 rows per request for standard plans)\n\nLet's get reviewing. Want me to evaluate the quality of the first row?`,
              isStreaming: false
            }
            : msg
        ));
        setIsProcessing(false);
        return;
      }

      // Handle final result - DON'T set pending changes here, only update UI message
      if (jsonResponse && jsonResponse.no_data) {
        setIsRefiningPrompt(true);
        setIsChatOpen(true);
        try {
          const relevantHistory = chatMessages.map(m => ({
            role: m.type === 'assistant' ? 'assistant' : 'user',
            content: m.content
          }));

          const response = await callClarificationAPI(relevantHistory, currentInput, user?.accessToken);

          if (response.success) {
            const assistantReply = response.result?.assistant_message || "Could you provide more details?";
            const isInvalid = assistantReply.includes('[INVALID]');
            const isReady = response.result?.ready || false;

            if (isInvalid) {
              setChatMessages(prev => prev.map(msg =>
                msg.id === processingMessageId
                  ? {
                    type: 'assistant',
                    content: '🚫 This is not a legal request. Generating illicit or harmful data is strictly forbidden.',
                    isStreaming: false,
                    isInvalidRequest: true
                  }
                  : msg
              ));
            } else {
              let actions = [
                {
                  label: 'Generate Now',
                  onClick: () => {
                    const userMessages = relevantHistory.filter(m => m.role === 'user').map(m => m.content).join('\n');
                    const combinedPrompt = userMessages ? `${userMessages}\n${currentInput}` : currentInput;
                    generateData(combinedPrompt);
                  }
                },
                {
                  label: 'Custom Order',
                  isPaid: true,
                  onClick: () => {
                    const userMessages = relevantHistory.filter(m => m.role === 'user').map(m => m.content).join('\n');
                    const combinedPrompt = userMessages ? `${userMessages}\n${currentInput}` : currentInput;
                    handleCustomOrder(combinedPrompt);
                  }
                }
              ];

              setChatMessages(prev => prev.map(msg =>
                msg.id === processingMessageId
                  ? {
                    type: 'assistant',
                    content: assistantReply,
                    isStreaming: false,
                    actions: actions
                  }
                  : msg
              ));
            }
          } else {
            setChatMessages(prev => prev.map(msg =>
              msg.id === processingMessageId
                ? {
                  type: 'assistant',
                  content: "Could not get a response. Please try generating or provide more details.",
                  isStreaming: false
                }
                : msg
            ));
          }
        } catch (error) {
          console.error("Refinement chat error:", error);
          setChatMessages(prev => prev.map(msg =>
            msg.id === processingMessageId
              ? {
                type: 'system',
                content: "Error communicating with AI assistant.",
                isStreaming: false
              }
              : msg
          ));
        }
      } else if (jsonResponse && jsonResponse.success) {
        const allChanges = jsonResponse.changes || { added: [], updated: [], deleted: [] };
        const totalChanges = (allChanges.added?.length || 0) +
          (allChanges.updated?.length || 0) +
          (allChanges.deleted?.length || 0);

        // For codegen mode (non-streaming), we still need to set pending changes
        if (!previewMode && totalChanges > 0) {
          console.log('[Phase] Changes detected from non-streaming response:', allChanges);
          setPendingChanges(allChanges);
          setPreviewMode(true);
        }

        const completionMessage = jsonResponse.message ||
          `Processing complete! ${totalChanges} total changes found. ${previewMode ? 'Review the changes above.' : 'Please review and Accept/Reject the changes.'}`;

        setChatMessages(prev => prev.map(msg =>
          msg.id === processingMessageId
            ? {
              type: 'assistant',
              content: completionMessage,
              isStreaming: false
            }
            : msg
        ));
      } else {
        setChatMessages(prev => prev.map(msg =>
          msg.id === processingMessageId
            ? {
              type: 'assistant',
              content: `The AI did not return any changes. Try refining your instruction.`,
              isStreaming: false
            }
            : msg
        ));
      }
    } catch (error) {
      console.error('Processing error:', error);
      setChatMessages(prev => prev.map(msg =>
        msg.id === processingMessageId
          ? {
            type: 'assistant',
            content: error.message || 'Could not process your request. Please try again.',
            isStreaming: false,
            // If it's the specific scaling restriction message, add the action even in error state
            ...(error.message && error.message.toLowerCase().includes('5k') ? {
              actions: [
                {
                  component: RazorpayCheckout,
                  props: {
                    amount: 5,
                    label: 'Get 5K rows for $5',
                    isScaleMode: true,
                    onSuccess: () => handleCustomOrder(`PAID SCALE: I want to scale my current generation to 5000 rows.`, 5000, true),
                    user: user
                  }
                }
              ]
            } : {})
          }
          : msg
      ));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAcceptChanges = () => {
    // Track accepted count for streaming delta logic
    streamingStateRef.current.addedCount += (pendingChanges.added?.length || 0);
    streamingStateRef.current.updatedCount += (pendingChanges.updated?.length || 0);
    streamingStateRef.current.deletedCount += (pendingChanges.deleted?.length || 0);

    console.log('[Accept] Starting to apply changes - Functional Update');

    // Capture changes to apply
    const additions = pendingChanges.added || [];
    const updates = pendingChanges.updated || [];
    const deletions = pendingChanges.deleted || [];
    const totalChanges = additions.length + updates.length + deletions.length;

    // Calculate initial target page (best effort)
    let targetPage = currentPage;
    let firstAffectedRowId = null;

    if (updates.length > 0) firstAffectedRowId = parseInt(updates[0].row_id);
    else if (deletions.length > 0) firstAffectedRowId = parseInt(deletions[0]);
    else if (additions.length > 0) firstAffectedRowId = additions[0]._rowId;

    // Update Headers (Functional)
    setHeaders(prevHeaders => {
      const newHeaderSet = new Set(prevHeaders);
      additions.forEach(row => Object.keys(row).forEach(k => k !== '_rowId' && newHeaderSet.add(k)));
      updates.forEach(u => Object.keys(u.changes).forEach(k => k !== '_rowId' && newHeaderSet.add(k)));
      return Array.from(newHeaderSet);
    });

    // Update CSV Data (Functional - GUARANTEES LATEST STATE)
    setCsvData(prevData => {
      let newData = [...prevData];

      // 1. Apply Additions
      additions.forEach((addedRow, idx) => {
        const newRow = { ...addedRow };
        if (newRow._rowId === undefined) {
          newRow._rowId = Date.now() + idx;
        }
        newData.push(newRow);
      });

      // 2. Apply Deletions
      if (deletions.length > 0) {
        const deletedIds = new Set(deletions.map(id => parseInt(String(id), 10)).filter(n => !isNaN(n)));
        newData = newData.filter(row => !deletedIds.has(row._rowId));
      }

      // 3. Apply Updates
      updates.forEach(update => {
        const targetId = parseInt(update.row_id);
        const rowIndex = newData.findIndex(row => row._rowId === targetId);
        if (rowIndex !== -1) {
          Object.entries(update.changes).forEach(([column, value]) => {
            let newValue = value;
            if (Array.isArray(value)) newValue = value[1];
            else if (value && typeof value === 'object') newValue = value.new;
            else newValue = value;

            newData[rowIndex] = { ...newData[rowIndex], [column]: newValue };
          });
        }
      });

      console.log('[Accept] Functional update complete. New length:', newData.length);
      return newData;
    });

    // Defer clearing preview state via setTimeout to ensure rendering happens
    setTimeout(() => {
      console.log('[Accept] Inside setTimeout - clearing preview state');
      setPendingChanges({ added: [], updated: [], deleted: [] });
      setPreviewMode(false);
      setCurrentPage(1);

      setChatMessages(prev => [...prev, {
        type: 'system',
        content: `Changes applied successfully! ${totalChanges} changes made.`
      }]);
    }, 0);
  };

  const handleRejectChanges = () => {
    // Track rejected count for streaming delta logic - treat as processed so we don't see them again
    streamingStateRef.current.addedCount += (pendingChanges.added?.length || 0);
    streamingStateRef.current.updatedCount += (pendingChanges.updated?.length || 0);
    streamingStateRef.current.deletedCount += (pendingChanges.deleted?.length || 0);

    setPendingChanges({ added: [], updated: [], deleted: [] });
    setPreviewMode(false);

    // Default to the first page as requested
    setCurrentPage(1);

    setChatMessages(prev => [...prev, { type: 'system', content: 'Changes rejected.' }]);
  };

  const handleAcceptAllChanges = () => {
    handleAcceptChanges();
  };

  const handleRejectAllChanges = () => {
    handleRejectChanges();
  };

  // Check if a cell has pending changes
  const getCellStyle = (row, column) => {
    let styles = '';

    if (!previewMode) {
      // Check for manual changes
      const manualKey = `${row._rowId}-${column}`;
      if (manualChanges[manualKey]) {
        styles += 'bg-yellow-50 border border-yellow-200 ring-1 ring-yellow-300 ';
      }
    } else {
      const rowIdStr = row._rowId != null ? String(row._rowId) : null;
      const isDeleted = rowIdStr && (pendingChanges.deleted || []).some(id => String(id) === rowIdStr);
      const isAdded = (pendingChanges.added || []).some(addedRow => addedRow._rowId === row._rowId);
      const updateChange = rowIdStr && (pendingChanges.updated || []).find(u =>
        u.row_id === rowIdStr && u.changes && u.changes[column]
      );

      if (isAdded) styles += 'bg-blue-50 border border-blue-200 ring-2 ring-blue-300 ';
      else if (isDeleted) styles += 'bg-red-50 border border-red-200 line-through opacity-60 ';
      else if (updateChange) styles += 'bg-emerald-50 border border-emerald-200 ring-2 ring-emerald-300 ';
    }

    return styles;
  };

  // Get the display value for a cell
  const getCellValue = (row, column) => {
    if (!previewMode) return row[column];

    const rowIdStr = row._rowId != null ? String(row._rowId) : null;
    const updateChange = rowIdStr && (pendingChanges.updated || []).find(u =>
      String(u.row_id) === rowIdStr && u.changes && u.changes[column] !== undefined
    );

    if (!updateChange) return row[column];
    const changeVal = updateChange.changes[column];
    if (Array.isArray(changeVal)) return changeVal[1];
    if (changeVal && typeof changeVal === 'object' && changeVal.new !== undefined) return changeVal.new;
    return changeVal;
  };

  const handleRowEdit = (row) => {
    setSelectedRow({ ...row });
    setRowEditInput('');
  };

  const handleRowCommand = async () => {
    if (!user) {
      showNotification('error', 'Please sign in to modify rows.');
      setShowAuthModal(true);
      return;
    }
    if (!rowEditInput.trim() || !selectedRow) return;

    setIsRowProcessing(true);
    try {
      // Create a temporary single-row dataset
      const tempDataset = [selectedRow];

      const payload = {
        "prompt": rowEditInput,
        "dataToClean": tempDataset
      };

      const response = await fetch(`${API_URL_AGENT}/api/clean-dataset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // Handle 401 errors automatically
      await handleResponseError(response);

      if (response.ok) {
        const result = await response.json();
        // Handle result (assuming codegen format for single row efficiency)
        if (result.success && result.changes && result.changes.updated.length > 0) {
          const update = result.changes.updated[0];
          const updatedFields = {};

          // Apply updates to selectedRow
          Object.entries(update.changes).forEach(([key, val]) => {
            // Handle {old, new} or [old, new] format
            let newValue = val;
            if (Array.isArray(val)) newValue = val[1];
            else if (typeof val === 'object' && val !== null) newValue = val.new;

            updatedFields[key] = newValue;
          });

          setSelectedRow(prev => ({ ...prev, ...updatedFields }));
        }
      } else if (response.status === 400) {
        try {
          const errBody = await response.json();
          if (errBody && errBody.datacreatoraimsg) {
            showNotification('error', errBody.datacreatoraimsg);
          }
        } catch (parseErr) {
          // ignore JSON parse failures
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsRowProcessing(false);
      setRowEditInput('');
    }
  };

  const saveRowChanges = () => {
    if (!selectedRow) return;

    setCsvData(prev => prev.map(row =>
      row._rowId === selectedRow._rowId ? selectedRow : row
    ));

    // Track manual changes for highlighting
    const originalRow = csvData.find(r => r._rowId === selectedRow._rowId);
    if (originalRow) {
      const changes = {};
      Object.keys(selectedRow).forEach(key => {
        if (selectedRow[key] !== originalRow[key]) {
          changes[`${selectedRow._rowId}-${key}`] = {
            oldValue: originalRow[key],
            newValue: selectedRow[key]
          };
        }
      });
      setManualChanges(prev => ({ ...prev, ...changes }));
    }

    setSelectedRow(null);
  };

  // Pagination calculations
  // In preview mode, merge pending added rows with existing data for display
  const displayedData = (() => {
    let base = csvData;
    if (previewMode && pendingChanges.added && pendingChanges.added.length > 0) {
      base = [...csvData, ...pendingChanges.added];
    }

    // Drop totally blank conversational rows so UI doesn't render them AND pagination aligns
    if (formatType?.toLowerCase() === 'chat_array') {
      const convKeys = headers.filter(h => h !== '_rowId' && /^(user|assistant|system)_\d+$/i.test(h));
      if (convKeys.length > 0) {
        base = base.filter(row => convKeys.some(h => row[h] != null && String(row[h]).trim().length > 0));
      }
    }
    return base;
  })();

  const totalPages = Math.ceil(displayedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = displayedData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Ensure current page is valid when data length changes (e.g. after deleting rows)
  useEffect(() => {
    const calculatedTotalPages = Math.ceil(displayedData.length / rowsPerPage);
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(calculatedTotalPages);
    }
  }, [displayedData.length, rowsPerPage, currentPage]);

  const exportCSV = () => {
    if (csvData.length === 0) return;

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => {
        const value = row[header];
        // Wrap in quotes if contains comma, quote, or newline
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'edited_data.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Headers to display in the grid. In preview mode, include any new columns from pending changes
  const displayedHeaders = (() => {
    const set = new Set(headers);
    if (previewMode && pendingChanges) {
      try {
        (pendingChanges.updated || []).forEach(update => {
          if (update && update.changes) {
            Object.keys(update.changes).forEach(col => {
              if (col !== '_rowId') set.add(col);
            });
          }
        });
        (pendingChanges.added || []).forEach(row => {
          if (row) {
            Object.keys(row).forEach(col => {
              if (col !== '_rowId') set.add(col);
            });
          }
        });
      } catch { }
    }
    return Array.from(set);
  })();

  const navigateRow = (direction) => {
    if (!selectedRow) return;
    const currentIndex = csvData.findIndex(row => row._rowId === selectedRow._rowId);
    if (currentIndex === -1) return;

    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < csvData.length) {
      setSelectedRow(csvData[newIndex]);
      setRowEditInput('');
    }
  };

  // Prepare CSV content for download
  const prepareCSVContent = (dataToProcess = csvData) => {
    const escapeCSV = (cell) => {
      if (cell == null) return '';
      cell = cell.toString();
      if (cell.includes('"') || cell.includes(',') || cell.includes('\n')) {
        return `"${cell.replace(/"/g, '""')}"`;
      }
      return cell;
    };

    // Filter out rows that have no content at all in any mapped header column
    const validData = dataToProcess.filter(row =>
      headers.some(h => row[h] != null && String(row[h]).trim().length > 0)
    );

    const hasRowIdCol = headers.some(h => h && (h.toLowerCase() === 'rowid' || h.toLowerCase() === 'rowids' || h === '_rowId'));
    const headerCols = hasRowIdCol ? headers : ['Row ID', ...headers];
    const csvHeader = headerCols.map(col => escapeCSV(col)).join(',') + '\n';
    const csvDataContent = validData.map(row => {
      const cells = hasRowIdCol ? headers.map(col => row[col]) : [row._rowId, ...headers.map(col => row[col])];
      return cells.map(cell => escapeCSV(cell)).join(',');
    }).join('\n');

    const BOM = '\uFEFF';
    return BOM + csvHeader + csvDataContent;
  };

  // Handle download from DownloadDropdown
  const handleDownload = (format) => {
    if (!user) {
      showNotification('error', 'Please sign in to download data.');
      setShowAuthModal(true);
      return;
    }

    if (csvData.length === 0) {
      showNotification('error', 'No data to download');
      return;
    }

    // Check if this is the first download
    const hasDownloadedBefore = localStorage.getItem('srishti_has_downloaded');
    const shouldShowFeedback = !hasDownloadedBefore;

    let dataToDownload = csvData;

    if (isGeneratedData && csvData.length > 500 && !hasPaidPremiumDownload) {
      dataToDownload = csvData.slice(0, 500);
      setPendingDownloadFormat(format);
      setShowPremiumDownloadModal(true);
      // DO NOT RETURN. Let the 500 rows proceed to download!
    }

    if (format === 'csv') {
      const csvContent = prepareCSVContent(dataToDownload);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8-sig;' });
      saveAs(blob, 'cleaned_data.csv');
      showNotification('success', 'CSV downloaded successfully!');
    } else if (format === 'json' || format === 'jsonl') {
      let jsonData;

      // Check if data was originally in JSONL chat format
      if (originalFileFormat === 'jsonl-chat') {
        // Convert back to original JSONL chat format
        jsonData = dataToDownload.map(row => {
          const messages = [];

          // Parse columns to rebuild messages array
          // Columns are in format: role_turnNumber (e.g., user_1, assistant_1)
          const messageMap = new Map(); // Map to store messages by their position

          Object.keys(row).forEach(col => {
            if (col === '_rowId') return;

            // Parse column name: role_turnNumber
            const match = col.match(/^(.+)_(\d+)$/);
            if (match) {
              const role = match[1];
              const turnNumber = parseInt(match[2]);
              const content = row[col];

              if (content != null && String(content).trim().length > 0) {
                // Store message with its original position
                if (!messageMap.has(col)) {
                  messageMap.set(col, { role, content, turnNumber, col });
                }
              }
            }
          });

          // Sort messages by their appearance in headers to maintain order
          const sortedMessages = Array.from(messageMap.values()).sort((a, b) => {
            const aIndex = headers.indexOf(a.col);
            const bIndex = headers.indexOf(b.col);
            return aIndex - bIndex;
          });

          // Build messages array
          sortedMessages.forEach(msg => {
            messages.push({
              role: msg.role,
              content: msg.content
            });
          });

          const hasRowIdCol = Object.keys(row).some(k => k && (k.toLowerCase() === 'rowid' || k.toLowerCase() === 'rowids'));
          const resultObj = {};
          if (!hasRowIdCol) {
            resultObj['rowId'] = row._rowId;
          }
          resultObj['messages'] = messages;
          return resultObj;
        }).filter(item => item.messages && item.messages.length > 0);

        // For JSONL, save as newline-delimited JSON
        const jsonlContent = jsonData.map(item => JSON.stringify(item)).join('\n');
        const blob = new Blob([jsonlContent], { type: 'application/jsonl' });
        saveAs(blob, 'cleaned_data.jsonl');
        showNotification('success', 'JSONL downloaded successfully!');
      } else {
        // Standard JSON export logic
        jsonData = dataToDownload.filter(row =>
          headers.some(h => row[h] != null && String(row[h]).trim().length > 0)
        ).map(row => {
          const nested = {};
          const hasRowIdCol = Object.keys(row).some(k => k && (k.toLowerCase() === 'rowid' || k.toLowerCase() === 'rowids'));
          if (!hasRowIdCol) {
            nested['rowId'] = row._rowId;
          }
          Object.keys(row).filter(k => k !== '_rowId').forEach(key => {
            const parts = key.split('.');
            let current = nested;
            for (let i = 0; i < parts.length - 1; i++) {
              const part = parts[i];
              if (!current[part]) current[part] = {};
              current = current[part];
            }
            let val = row[key];
            if (typeof val === 'string' && (val.trim().startsWith('[') || val.trim().startsWith('{'))) {
              try { val = JSON.parse(val); } catch (e) { }
            }
            current[parts[parts.length - 1]] = val;
          });
          return nested;
        });

        if (format === 'jsonl') {
          const jsonlContent = jsonData.map(item => JSON.stringify(item)).join('\n');
          const blob = new Blob([jsonlContent], { type: 'application/jsonl' });
          saveAs(blob, 'cleaned_data.jsonl');
          showNotification('success', 'JSONL downloaded successfully!');
        } else {
          const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
          saveAs(blob, 'cleaned_data.json');
          showNotification('success', 'JSON downloaded successfully!');
        }
      }
    } else if (format === 'hf') {
      showNotification('info', 'HuggingFace upload coming soon!');
      return; // Don't show feedback for unimplemented feature
    }

    // Fire-and-forget download telemetry
    requestDownload(csvData, user, `cleaned_data.${format}`);

    // Show feedback popup on first download
    if (shouldShowFeedback) {
      localStorage.setItem('srishti_has_downloaded', 'true');
      localStorage.setItem('srishti_feedback_shown', 'true');
      // Show popup after a short delay to let the download notification appear first
      setTimeout(() => {
        setShowFeedbackPopup(true);
      }, 1000);
    }
  };

  // Open Request Review Popup
  const openRequestReviewPopup = () => {
    setIsRequestReview(true);
  };

  // Handle Submit Review
  const handleSubmitReview = async (type, evaluation_instructions) => {
    if (!user) {
      showNotification('error', 'Please sign in to submit a review request.');
      setShowAuthModal(true);
      return;
    }
    var final_comments = evaluation_instructions;
    if (type === 'feedback') {
      showNotification('success', "Thank you for submitting your feedback. Click on Request Review if you need any changes.");
    } else {
      final_comments = "Review Request:" + final_comments;
      setIsRequestReview(false);
      showNotification('success', "Thank you for placing your request. We will get back to you within 24 hours.");
    }

    // Prepare CSV content
    const csvContent = prepareCSVContent();
    const formData = new FormData();
    formData.append('csvData', csvContent);
    formData.append('userId', user.uid);
    formData.append('evalComments', final_comments);
    formData.append('colNames', headers.join(','));
    formData.append('userPrompt', lastUsedPrompt || generatePrompt || 'Data Cleaning Request');

    try {
      await fetch(`${API_URL}/api/evaluate-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user.accessToken}` },
        body: formData
      });
    } catch (error) {
      showNotification('error', 'Error sending review request');
      console.error('Error:', error);
    }
  };

  // Start Interactive Generation Mode
  const startInteractiveGeneration = () => {
    if (!generatePrompt.trim()) {
      showNotification('error', 'Please enter a description first.');
      return;
    }

    setShowGenerateModal(false);
    setIsChatOpen(true);
    setIsInteractiveGeneration(true);

    // Add User Prompt
    setChatMessages(prev => [...prev, { type: 'user', content: `Start interactive generation for: ${generatePrompt}` }]);

    // Mock "Planning" phase - In real app, call a planning endpoint
    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        type: 'assistant',
        content: `I can help you build this dataset. Based on "${generatePrompt}", I suggest we include columns for **Category, Description, Value, and Status**.\n\nI'll generate roughly ${generateNumber} rows covering main topics. Shall I proceed?`,
        actions: [
          { label: 'Yes, Generate', onClick: () => generateData() },
          { label: 'Edit Prompt', onClick: () => setShowGenerateModal(true) } // Re-open modal to edit
        ]
      }]);
    }, 600);
  };

  const openAIEvaluation = async () => {
    if (!user) {
      showNotification('error', 'Please sign in to evaluate data.');
      setShowAuthModal(true);
      return;
    }

    setIsAIEvalOpen(true);
    setIsAIEvalLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/quality-eval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          activity_id: activityId,
          user_prompt: lastUsedPrompt || generatePrompt
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const evalData = data.quality_eval
        ? (typeof data.quality_eval === 'string' ? JSON.parse(data.quality_eval) : data.quality_eval)
        : data;
      setAIEvaluation(evalData);
    } catch (error) {
      console.error('Error fetching AI Evaluation:', error);
      showNotification('error', 'Failed to fetch AI Evaluation.');
      setIsAIEvalOpen(false);
    } finally {
      setIsAIEvalLoading(false);
    }
  }

  // Auto-trigger quality evaluation for chat
  const triggerAutoQualityEval = async (id, prompt, generatedData = null) => {
    if (!user || !id) return;

    // Add a temporary loading message to the chat
    setChatMessages(prev => [...prev, {
      type: 'system',
      content: 'AI is auditing data quality... 📊',
      isQualityLoading: true
    }]);

    console.log('Triggering Auto Quality Eval for activity:', id);
    try {
      const payload = {
        activity_id: id,
        user_topic: prompt || 'Synthetic Data Generation' // Changed from user_prompt to user_topic
      };

      // Pass the entire generated data directly to bypass the DB race condition
      if (generatedData && generatedData.length > 0) {
        console.log(`Sending ${generatedData.length} rows for evaluation`);
        payload.generated_data = generatedData;
      }

      const response = await fetch(`${API_URL}/api/quality-eval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        setChatMessages(prev => prev.filter(m => !m.isQualityLoading));
        return;
      }

      console.log('Received response from quality-eval:', response.status);
      const data = await response.json();
      console.log('Quality Eval Data:', data);

      let evalData;
      try {
        evalData = data.quality_eval
          ? (typeof data.quality_eval === 'string' ? JSON.parse(data.quality_eval) : data.quality_eval)
          : data;
      } catch (parseError) {
        console.error('Failed to parse quality_eval JSON:', parseError);
        evalData = data.quality_eval || data;
      }

      const isStructuredReport = evalData && typeof evalData === 'object' &&
        (evalData.hasOwnProperty('final_score') ||
          evalData.hasOwnProperty('scores') ||
          evalData.hasOwnProperty('quality_eval'));

      // Replace the loading message with the actual quality report
      setChatMessages(prev => {
        const filtered = prev.filter(m => !m.isQualityLoading);

        if (!isStructuredReport) {
          // If it's just a text message or an empty result, show it directly or show a fallback
          const displayContent = typeof evalData === 'string' ? evalData :
            (typeof evalData === 'object' && Object.keys(evalData).length > 0 ? JSON.stringify(evalData) :
              "I encountered an issue while generating the quality report, but your data is ready!");

          return [...filtered, {
            type: 'assistant',
            content: displayContent
          }];
        }

        return [...filtered, 
          {
            type: 'assistant',
            content: `I've completed an automated quality audit of the generated data.`,
            actions: [
              {
                component: QualityReportInline,
                props: {
                  data: evalData,
                  onDetailClick: () => {
                    setAIEvaluation(evalData);
                    setIsAIEvalOpen(true);
                  },
                  onAddTopic: (topicName) => {
                    const basePrompt = prompt || lastUsedPrompt || generatePrompt;
                    const currentColumns = headersRef.current.filter(h => h !== "_rowId").join(", ");
                    const newPrompt = `${basePrompt}. Also include detailed data about ${topicName}. Maintain the existing structure with these columns: ${currentColumns}`;

                    // Calculate number of points: 20 per topic, max 100
                    const topics = topicName.split(',').map(t => t.trim()).filter(t => t.length > 0);
                    const numPoints = Math.min(topics.length * 20, 100);

                    // Use a small delay to ensure chat doesn't clear during re-generation or handle state properly
                    generateData(newPrompt, true, numPoints);
                  }
                }
              }
            ]
          },
          {
            type: 'assistant',
            content: `You can see the past data sessions in the Data History page, and load the data into the grid and work on it.`,
            actions: [
              {
                label: 'Data History',
                onClick: () => navigate('/datahistory')
              }
            ]
          }
        ];
      });

      // Ensure chat is open so message is visible
      setIsChatOpen(true);

      // Also update local state so the top button works immediately
      if (isStructuredReport) {
        setAIEvaluation(evalData);
      }
    } catch (error) {
      console.error('Auto Quality Eval Error:', error);
      // Clean up loading message on error
      setChatMessages(prev => prev.filter(m => !m.isQualityLoading));
    }
  };

  // Open Model Leaderboard
  const openLeaderboard = () => {
    if (!user) {
      showNotification('error', 'Please sign in to evaluate data.');
      setShowAuthModal(true);
      return;
    }
    const columns = headers.filter(h => h !== '_rowId');
    navigate("/evalleader", {
      state: {
        userDetails: user.accessToken,
        dataToEvaluate: csvData,
        targetColumns: columns
      }
    });
  };
  const detectNestedJSON = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return false;
    for (let i = 0; i < Math.min(dataArray.length, 5); i++) {
      const item = dataArray[i];
      for (const key in item) {
        if (key === '_rowId') continue;
        const val = item[key];
        if (val && typeof val === 'object') return true;
        if (typeof val === 'string' && (val.trim().startsWith('{') || val.trim().startsWith('['))) {
          try {
            const parsed = JSON.parse(val);
            if (parsed && typeof parsed === 'object') return true;
          } catch (e) { }
        }
      }
    }
    return false;
  };

  useEffect(() => {
    if (csvData.length > 0 && formatType?.toLowerCase().includes('json')) {
      if (detectNestedJSON(csvData)) {
        setIsDedicatedJSONView(true);
      } else {
        setIsDedicatedJSONView(false);
      }
    } else {
      setIsDedicatedJSONView(false);
    }
  }, [csvData, formatType]);

  if (isDedicatedJSONView && csvData.length > 0) {
    return (
      <NestedJSONViewerPage
        user={user}
        data={csvData}
        headers={headers}
        onBack={() => setIsDedicatedJSONView(false)}
      />
    );
  }

  return (
    <div className="flex h-[calc(100vh-52px)] font-inter relative bg-gray-50 overflow-hidden">
      {/* Main CSV Editor Panel */}
      <div className="flex flex-col flex-1 min-w-0 h-full">
        {/* Compact Header */}
        <div className="flex-none px-4 py-2 border-b border-gray-200 bg-white z-40">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-4">
              <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-blue-700 drop-shadow-sm" style={{ WebkitBackgroundClip: 'text' }}>Srishti</h1>
              <p className="text-blue-600 font-medium text-sm tracking-wide">Your Wise Agent for Data Creation & Cleaning</p>
            </div>
            <div className="flex gap-3">
              <div id="download-dropdown-wrapper">
                <DownloadDropdown
                  isDisabled={csvData.length === 0}
                  onDownload={handleDownload}
                />
              </div>
              <button
                id="upload-file-btn"
                onClick={() => fileInputRef.current?.click()}
                className="group flex items-center gap-2 px-5 py-2.5 bg-white text-blue-600 border border-blue-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Upload size={18} className="group-hover:scale-105 transition-transform duration-200" />
                <span className="text-sm font-medium">Clean Your File</span>
              </button>
              <button
                id="clean-with-ai-btn"
                onClick={() => {
                  if (csvData.length === 0) {
                    setShowFormatModal(true);
                  } else {
                    setIsChatOpen(true);
                  }
                }}
                className="group flex items-center gap-2 px-5 py-2.5 bg-blue-800 text-white rounded-lg font-medium hover:bg-blue-900 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Bot size={18} className="group-hover:scale-105 transition-transform duration-200" />
                <span className="text-sm font-medium">{csvData.length > 0 ? 'Improve with AI' : 'Create with AI'}</span>
              </button>
              <button
                onClick={resetTour}
                className="group flex items-center gap-2 px-4 py-2.5 bg-white text-blue-600 border border-blue-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <CircleHelp size={18} className="group-hover:scale-105 transition-transform duration-200" />
                <span className="text-sm font-medium">Guide</span>
              </button>
              <button
                onClick={handleClearSession}
                className="group flex items-center gap-2 px-4 py-2.5 bg-white text-red-600 border border-red-200 hover:border-red-500 hover:bg-red-50 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <RefreshCcw size={18} className="group-hover:scale-105 transition-transform duration-200" />
                <span className="text-sm font-medium">Clear All</span>
              </button>

              {/* <button
                onClick={() => navigate('/datahistory')}
                className="group flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 border border-gray-200 hover:border-gray-500 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <History size={18} className="group-hover:scale-105 transition-transform duration-200" />
                <span className="text-sm font-medium">History</span>
              </button> */}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json,.jsonl"
            onChange={handleFileUpload}
            className="hidden"
          />
          <input
            ref={contextInputRef}
            type="file"
            accept=".csv,.pdf"
            onChange={handleContextFileUpload}
            className="hidden"
          />
        </div>

        {/* Main Content split */}
        <div className="flex-1 flex min-h-0 relative">

          {/* Left Side: Data Grid */}
          <div className="flex-1 flex flex-col min-w-0 relative">
            {/* Preview Actions */}
            {previewMode && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <span className="text-sm font-medium text-gray-800">Preview Changes:</span>
                    <div className="flex items-center gap-4">
                      {pendingChanges.deleted.length > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          <span className="text-sm font-medium text-red-700">{pendingChanges.deleted.length} rows to delete</span>
                        </div>
                      )}
                      {pendingChanges.updated.length > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                          <span className="text-sm font-medium text-emerald-700">{pendingChanges.updated.length} cells to update</span>
                        </div>
                      )}
                      {pendingChanges.added.length > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-medium text-blue-700">{pendingChanges.added.length} rows to add</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Manual Changes Indicator */}
            {!previewMode && Object.keys(manualChanges).length > 0 && (
              <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200">
                <div className="flex items-center gap-4">
                  <Edit3 size={16} className="text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    You have {Object.keys(manualChanges).length} unsaved manual edit(s)
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span className="text-sm text-yellow-700">Yellow cells show your edits</span>
                  </div>
                </div>
              </div>
            )}

            {!previewMode && duplicatesInfo && duplicatesInfo.duplicatesCount > 0 && (
              <div className="p-4 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* <Copy size={16} className="text-blue-600" /> */}
                    <span className="text-sm font-medium text-blue-800">
                      {duplicatesInfo.duplicatesCount} duplicate row{duplicatesInfo.duplicatesCount > 1 ? 's' : ''} removed
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-blue-700">Duplicates filtered out</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowDuplicatesModal(true)}
                      className="px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                    >
                      View Duplicates
                    </button>
                    <button
                      onClick={() => {
                        // Restore original data with duplicates
                        const dataToUse = duplicatesInfo.originalData;
                        setCsvData(dataToUse);
                        setDuplicatesInfo(null);
                        setManualChanges({});
                        setShowDuplicatesModal(false);
                        // Update message
                        setChatMessages([{
                          type: 'system',
                          content: `Duplicates restored. Showing ${duplicatesInfo.originalData.length} rows including duplicates.`
                        }]);
                      }}
                      className="px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                    >
                      Restore Duplicates
                    </button>
                    <button
                      onClick={() => setDuplicatesInfo(null)}
                      className="p-1 text-blue-700 hover:bg-blue-200 rounded-md transition-colors"
                      title="Close"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Generation loading overlay — shown before first row arrives */}
            {isGenerating && csvData.length === 0 && (() => {
              const steps = [
                { label: 'Queued', sublabel: 'Analyzing your request', icon: '🧠' },
                { label: 'Verifying', sublabel: 'Checking for harmful content', icon: '🔍' },
                { label: 'Researching', sublabel: 'Coming up with relevant sub-topics', icon: '🔍' },
                { label: 'Preprocessing', sublabel: 'Removing duplicates', icon: '📐' },
                { label: 'Generating', sublabel: 'Streaming first rows', icon: '⚡' },
              ];
              return (
                <div className="flex-1 flex flex-col items-center justify-center gap-10 pb-32 px-8">
                  {/* Spinner */}
                  <div className="relative w-16 h-16">
                    <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
                  </div>
                  {/* Step list */}
                  <div className="flex flex-col gap-3 w-full max-w-xs">
                    {steps.map((step, idx) => {
                      const isDone = idx < genLoadingStep;
                      const isActive = idx === genLoadingStep;
                      return (
                        <div key={idx} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-500 ${isActive ? 'bg-blue-50 border border-blue-200 shadow-sm'
                          : isDone ? 'opacity-40'
                            : 'opacity-20'
                          }`}>
                          <span className="text-lg">{step.icon}</span>
                          <div className="flex flex-col">
                            <span className={`text-sm font-medium ${isActive ? 'text-blue-700' : 'text-gray-600'}`}>{step.label}</span>
                            <span className="text-xs text-gray-400">{step.sublabel}</span>
                          </div>
                          {isDone && (
                            <svg className="ml-auto w-4 h-4 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {isActive && (
                            <div className="ml-auto flex gap-0.5">
                              <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400">This may take a moment{includeSearch || contextFile ? ' — extra context is being processed' : ''}…</p>
                </div>
              );
            })()}

            {/* ── Loading banner: data is streaming in (grid already has rows) ── */}
            {isGenerating && csvData.length > 0 && (
              <LoadingSpinner
                message="Generating data"
                subMessage={`${csvData.length} rows received so far, streaming more…`}
                variant="banner"
              />
            )}

            {/* ── Loading banner: AI is processing a cleaning / chat command ── */}
            {isProcessing && csvData.length > 0 && (
              <LoadingSpinner
                message="Srishti is processing your request"
                subMessage="Changes will appear in the grid when ready"
                variant="banner"
              />
            )}

            {/* ── Loading banner: AI is preparing quality report ── */}
            {isAIEvalLoading && (
              <LoadingSpinner
                message="Preparing quality report"
                subMessage="Analyzing your dataset and generating insights..."
                variant="banner"
              />
            )}

            {/* Data Summary with Actions */}
            {csvData.length > 0 && (
              <div className="px-4 py-1.5 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  {/* Left: Row/Column/Token counts */}
                  <div className="flex items-center gap-4">
                    <div className="px-3 py-1 bg-gray-200 rounded-full">
                      <span className="text-gray-700 font-medium text-sm">
                        {displayedData.length} rows
                      </span>
                    </div>
                    <div className="px-3 py-1 bg-gray-200 rounded-full">
                      <span className="text-gray-700 font-medium text-sm">
                        {previewMode ? displayedHeaders.length : headers.length} columns
                      </span>
                    </div>
                    {(() => {
                      // Estimate tokens: concatenate all cell values and use the
                      // standard ~4-chars-per-token approximation (same as OpenAI playground).
                      // tiktoken (WASM) would be more accurate but adds ~1 MB and async init;
                      // this heuristic is typically within ~5% for English text.
                      const totalChars = csvData.reduce((acc, row) => {
                        return acc + Object.entries(row)
                          .filter(([k]) => k !== '_rowId')
                          .reduce((s, [, v]) => s + String(v == null ? '' : v).length, 0);
                      }, 0);
                      const estimatedTokens = Math.round(totalChars / 4);
                      const formatted = estimatedTokens >= 1000
                        ? `${(estimatedTokens / 1000).toFixed(1)}k`
                        : String(estimatedTokens);
                      return (
                        <div className="px-3 py-1 bg-violet-100 rounded-full" title="Estimated token count (~4 chars per token)">
                          <span className="text-violet-700 font-medium text-sm">
                            ~{formatted} tokens
                          </span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Center: Pagination */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      title="Go to first page"
                    >
                      <SkipBack size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      title="Previous page"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="px-3 py-1 text-sm font-medium text-gray-700">
                      {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      title="Next page"
                    >
                      <ChevronRight size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      title="Go to last page"
                    >
                      <SkipForward size={16} />
                    </button>
                  </div>

                  {/* Right: Action buttons */}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200">
                    <button
                      id="request-review-btn"
                      onClick={() => openRequestReviewPopup()}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                    >
                      Request Review
                    </button>
                    <div className="w-px h-5 bg-gray-300"></div>
                    {searchResults.length > 0 && (
                      <>
                        <button
                          onClick={() => setShowSourcesModal(true)}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200 flex items-center gap-1.5"
                        >
                          <Globe size={14} />
                          View Sources
                        </button>
                      </>
                    )}
                    <div className="w-px h-5 bg-gray-300"></div>
                    <button
                      onClick={openLeaderboard}
                      disabled={true}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Leaderboard
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        Coming Soon
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Data Grid with Search & Sort */}
            <div className="flex-1 overflow-hidden relative">
              {csvData.length > 0 ? (
                <>
                  {/* OLD CODE COMMENTED OUT
                  <DataGrid
                    data={displayedData}
                    headers={displayedHeaders}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    previewMode={previewMode}
                    pendingChanges={pendingChanges}
                    manualChanges={manualChanges}
                    editingCell={editingCell}
                    editValue={editValue}
                    hoveredCell={hoveredCell}
                    onRowEdit={handleRowEdit}
                    onCellClick={startEditing}
                    onCellHover={setHoveredCell}
                    onCellHoverEnd={() => setHoveredCell(null)}
                    onEditValueChange={setEditValue}
                    onSaveEdit={saveEdit}
                    onCancelEdit={cancelEditing}
                    getOldValue={getOldValue}
                    getCellValue={getCellValue}
                    shouldUseTextarea={shouldUseTextarea}
                  />
                  */}

                  {formatType?.toLowerCase() === 'conversational' || formatType?.toLowerCase() === 'conversational_data' || formatType?.toLowerCase() === 'chat_array' ? (
                    <ConversationalGrid
                      data={displayedData}
                      headers={displayedHeaders}
                      currentPage={currentPage}
                      rowsPerPage={rowsPerPage}
                      previewMode={previewMode}
                      pendingChanges={pendingChanges}
                      manualChanges={manualChanges}
                      editingCell={editingCell}
                      editValue={editValue}
                      hoveredCell={hoveredCell}
                      onRowEdit={handleRowEdit}
                      onCellClick={startEditing}
                      onCellHover={setHoveredCell}
                      onCellHoverEnd={() => setHoveredCell(null)}
                      onEditValueChange={setEditValue}
                      onSaveEdit={saveEdit}
                      onCancelEdit={cancelEditing}
                      getOldValue={getOldValue}
                      getCellValue={getCellValue}
                      shouldUseTextarea={shouldUseTextarea}
                    />
                  ) : formatType.includes('json') ? (
                    <NestedJSONGrid
                      data={displayedData}
                      headers={displayedHeaders}
                      currentPage={currentPage}
                      rowsPerPage={rowsPerPage}
                      previewMode={previewMode}
                      pendingChanges={pendingChanges}
                      manualChanges={manualChanges}
                      editingCell={editingCell}
                      editValue={editValue}
                      hoveredCell={hoveredCell}
                      onRowEdit={handleRowEdit}
                      onCellClick={startEditing}
                      onCellHover={setHoveredCell}
                      onCellHoverEnd={() => setHoveredCell(null)}
                      onEditValueChange={setEditValue}
                      onSaveEdit={saveEdit}
                      onCancelEdit={cancelEditing}
                      getOldValue={getOldValue}
                      getCellValue={getCellValue}
                      shouldUseTextarea={shouldUseTextarea}
                    />
                  ) : (
                    <DataGrid
                      data={displayedData}
                      headers={displayedHeaders}
                      currentPage={currentPage}
                      rowsPerPage={rowsPerPage}
                      previewMode={previewMode}
                      pendingChanges={pendingChanges}
                      manualChanges={manualChanges}
                      editingCell={editingCell}
                      editValue={editValue}
                      hoveredCell={hoveredCell}
                      onRowEdit={handleRowEdit}
                      onCellClick={startEditing}
                      onCellHover={setHoveredCell}
                      onCellHoverEnd={() => setHoveredCell(null)}
                      onEditValueChange={setEditValue}
                      onSaveEdit={saveEdit}
                      onCancelEdit={cancelEditing}
                      getOldValue={getOldValue}
                      getCellValue={getCellValue}
                      shouldUseTextarea={shouldUseTextarea}
                    />
                  )}

                  {/* Compact Floating Accept/Reject Buttons */}
                  {previewMode && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
                      <div className="bg-white shadow-lg border border-gray-200 rounded-full p-1.5 flex items-center gap-2 pointer-events-auto transform transition-all hover:scale-105">
                        <button
                          onClick={handleAcceptAllChanges}
                          title="Accept All Changes"
                          className="w-9 h-9 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors shadow-sm"
                        >
                          <CheckCircle2 size={18} />
                        </button>
                        <button
                          onClick={handleRejectAllChanges}
                          title="Reject All Changes"
                          className="w-9 h-9 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-sm"
                        >
                          <XCircle size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (

                <div className="bg-white rounded-xl border border-dashed border-gray-300 h-full flex flex-col items-center justify-center p-12 text-center m-6">
                  <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6 shadow-sm">
                    <Wand2 size={32} className="text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Get Started</h3>
                  <p className="text-gray-500 max-w-md mb-8">Generate new data with AI or upload a CSV/JSON file to get started.</p>
                  <div className="flex gap-4 flex-wrap justify-center">
                    <button
                      onClick={() => setShowFormatModal(true)}
                      disabled={isGenerating}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      <Wand2 size={18} />
                      Generate Data
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-6 py-3 bg-white text-blue-600 border border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 font-medium transition-all shadow-sm flex items-center gap-2"
                    >
                      <Upload size={18} />
                      Clean Your File
                    </button>
                    <button
                      onClick={() => navigate('/datahistory')}
                      className="px-6 py-3 bg-white text-blue-600 border border-gray-300 rounded-lg hover:border-gray-400 font-medium transition-all shadow-sm flex items-center gap-2"
                    >
                      <History size={18} />
                      Data History
                    </button>
                  </div>
                </div>
              )}

            </div>

            {/* Fixed Pagination */}
            {csvData.length > 0 && totalPages > 1 && (
              <div className="p-4 border-t border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1}-{Math.min(endIndex, displayedData.length)} of {displayedData.length} rows
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      title="Go to first page"
                    >
                      <SkipBack size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      title="Previous page"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="px-3 py-1 text-sm font-medium text-gray-700">
                      {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      title="Next page"
                    >
                      <ChevronRight size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      title="Go to last page"
                    >
                      <SkipForward size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Right Side: Tabbed Sidebar (AI Chat + Analytics) */}
      <RightSidebar
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        // Chat props
        chatMessages={chatMessages}
        isProcessing={isProcessing}
        isGenerating={isGenerating}
        currentInput={currentInput}
        setCurrentInput={setCurrentInput}
        handleSendMessage={handleSendMessage}
        isRecording={isRecording}
        isTranscribing={isTranscribing}
        toggleRecording={toggleRecording}
        contextFile={contextFile}
        onContextFileClick={() => contextInputRef.current?.click()}
        onRemoveContextFile={() => { setContextFile(null); setColumnNames(''); }}
        includeSearch={includeSearch}
        setIncludeSearch={setIncludeSearch}
        isGenerationMode={csvData.length === 0}
        isConversationalFormat={originalFileFormat === 'jsonl-chat'}
        userSelectedFormat={userSelectedFormat}
        setUserSelectedFormat={setUserSelectedFormat}
        // Analytics props
        data={csvData}
        headers={headers}
      />

      {/* Row Editor Modal */}
      {
        selectedRow && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100/50 rounded-lg text-blue-600">
                    <Edit3 size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Edit Row #{selectedRow._rowId}</h2>
                    <p className="text-sm text-gray-500">Make manual edits or ask AI to help</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex bg-gray-100 rounded-lg p-1 mr-2 gap-1">
                    <button
                      onClick={() => navigateRow(-1)}
                      disabled={csvData.findIndex(r => r._rowId === selectedRow._rowId) === 0}
                      className="p-1 hover:bg-white hover:shadow-sm rounded transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:shadow-none"
                      title="Previous Row"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      onClick={() => navigateRow(1)}
                      disabled={csvData.findIndex(r => r._rowId === selectedRow._rowId) === csvData.length - 1}
                      className="p-1 hover:bg-white hover:shadow-sm rounded transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:shadow-none"
                      title="Next Row"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>

                  <button
                    onClick={() => setSelectedRow(null)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveRowChanges}
                    className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2"
                  >
                    <Save size={16} />
                    Save Changes
                  </button>
                </div>
              </div>

              {/* Content - Form Grid */}
              <div className="flex-1 overflow-y-auto p-6 bg-white">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {headers.map(header => (
                    <div key={header} className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide ml-1">
                        {header}
                      </label>
                      {shouldUseTextarea(selectedRow[header]) ? (
                        <textarea
                          value={selectedRow[header] || ''}
                          onChange={e => setSelectedRow(prev => ({ ...prev, [header]: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm min-h-[100px] resize-y"
                        />
                      ) : (
                        <input
                          type="text"
                          value={selectedRow[header] || ''}
                          onChange={e => setSelectedRow(prev => ({ ...prev, [header]: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Assistant Footer */}
              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <div className="flex gap-3 items-center">
                  <div className="flex-none p-2 bg-blue-100 rounded-full text-blue-600">
                    <Sparkles size={18} />
                  </div>
                  {/* <div className="flex-1 relative">
                    <input
                      type="text"
                      value={rowEditInput}
                      onChange={e => setRowEditInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleRowCommand()}
                      placeholder="Ask AI to change this row (e.g., 'Format phone number', 'Capitalize name')"
                      className="w-full pl-4 pr-12 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm text-sm"
                    />
                    <button
                      onClick={handleRowCommand}
                      disabled={isRowProcessing || !rowEditInput.trim()}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRowProcessing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* View Duplicates Modal */}
      {
        showDuplicatesModal && duplicatesInfo && duplicatesInfo.duplicateRows && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[80vh]">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-blue-100/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600 rounded-lg text-white">
                    <AlertCircle size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Duplicate Rows</h2>
                    <p className="text-sm text-gray-500">{duplicatesInfo.duplicateRows.length} duplicate row(s) will be removed</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDuplicatesModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-md"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content - Scrollable Table */}
              <div className="flex-1 overflow-auto p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">
                          #
                        </th>
                        {headers.map((header, idx) => (
                          <th key={idx} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b whitespace-nowrap">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {duplicatesInfo.duplicateRows.map((row, rowIdx) => (
                        <tr key={rowIdx} className="bg-red-50 hover:bg-red-100 transition-colors">
                          <td className="px-3 py-2 text-gray-500 font-medium">
                            {row._rowId || rowIdx + 1}
                          </td>
                          {headers.map((header, cellIdx) => (
                            <td key={cellIdx} className="px-3 py-2 text-gray-700 max-w-xs truncate" title={String(row[header] || '')}>
                              {String(row[header] || '').length > 50
                                ? String(row[header] || '').substring(0, 50) + '...'
                                : String(row[header] || '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {duplicatesInfo.duplicateRows.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No duplicate rows to display
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={() => setShowDuplicatesModal(false)}
                  className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    const dataToUse = duplicatesInfo.originalData;
                    setCsvData(dataToUse);
                    setDuplicatesInfo(null);
                    setManualChanges({});
                    setShowDuplicatesModal(false);
                    setChatMessages([{
                      type: 'system',
                      content: `Duplicates restored. Showing ${duplicatesInfo.originalData.length} rows including duplicates.`
                    }]);
                  }}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Restore All Duplicates
                </button>
              </div>
            </div>
          </div>
        )
      }



      {/* Custom Stylish Notification */}
      {
        notification && (
          <div
            key={notification.id}
            className={`fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl border backdrop-blur-sm animate-in slide-in-from-right duration-300 ${notification.type === 'success'
              ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200 text-emerald-800'
              : notification.type === 'error'
                ? 'bg-gradient-to-r from-red-50 to-rose-50 border-red-200 text-red-800'
                : notification.type === 'info'
                  ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-800'
                  : 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 text-amber-800'
              }`}
          >
            <div className={`p-2 rounded-lg ${notification.type === 'success'
              ? 'bg-emerald-100'
              : notification.type === 'error'
                ? 'bg-red-100'
                : notification.type === 'info'
                  ? 'bg-blue-100'
                  : 'bg-amber-100'
              }`}>
              {notification.type === 'success' && <CheckCircle2 size={20} className="text-emerald-600" />}
              {notification.type === 'error' && <XCircle size={20} className="text-red-600" />}
              {notification.type === 'info' && <Info size={20} className="text-blue-600" />}
              {notification.type === 'warning' && <AlertCircle size={20} className="text-amber-600" />}
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="p-1 hover:bg-black/5 rounded-md transition-colors"
            >
              <X size={16} className="opacity-60" />
            </button>
          </div>
        )
      }

      {/* Request Review Popup */}
      <RequestReviewPopup
        open={isRequestReview}
        onClose={() => setIsRequestReview(false)}
        onProceed={handleSubmitReview}
      />

      {/* Feedback Popup */}
      <FeedbackPopup
        user={user}
        open={showFeedbackPopup}
        onClose={() => setShowFeedbackPopup(false)}
        title="How was your experience?"
        subtitle="We'd love to hear your feedback on Srishti!"
      />

      <DataAnalysisPopup isOpen={isAIEvalOpen}
        onClose={() => setIsAIEvalOpen(false)}
        data={aievaluation}
      />

      <Joyride
        steps={tourSteps}
        run={runTour}
        stepIndex={tourStepIndex}
        continuous={true}
        showSkipButton={true}
        showProgress={true}
        scrollToFirstStep={true}
        disableOverlayClose={true}
        spotlightClicks={false}
        callback={handleJoyrideCallback}
        floaterProps={{
          styles: {
            floater: {
              filter: 'drop-shadow(0 10px 25px rgba(0, 0, 0, 0.15))',
            }
          }
        }}
        styles={{
          options: {
            primaryColor: '#2563EB',
            zIndex: 10000,
            arrowColor: '#ffffff',
            backgroundColor: '#ffffff',
            overlayColor: 'rgba(15, 23, 42, 0.75)',
            textColor: '#1e293b',
          },
          tooltip: {
            borderRadius: '16px',
            padding: '20px 24px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          },
          tooltipContainer: {
            textAlign: 'left',
          },
          tooltipTitle: {
            fontSize: '18px',
            fontWeight: '700',
            color: '#0f172a',
            marginBottom: '8px',
          },
          tooltipContent: {
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#475569',
          },
          buttonNext: {
            backgroundColor: '#2563EB',
            borderRadius: '10px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '600',
            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
            transition: 'all 0.2s ease',
          },
          buttonBack: {
            color: '#2563EB',
            fontSize: '14px',
            fontWeight: '500',
            marginRight: '12px',
          },
          buttonSkip: {
            color: '#94a3b8',
            fontSize: '13px',
            fontWeight: '500',
          },
          spotlight: {
            borderRadius: '12px',
            boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.3)',
          },
          beaconInner: {
            backgroundColor: '#2563EB',
          },
          beaconOuter: {
            backgroundColor: 'rgba(37, 99, 235, 0.3)',
            borderColor: '#2563EB',
          },
        }}
        locale={{
          back: '← Back',
          close: 'Close',
          last: 'Get Started →',
          next: 'Next →',
          skip: 'Skip Tour',
        }}
        scrollOffset={150}
      />

      <UpgradePopup
        isOpen={showUpgradePopup}
        onClose={() => setShowUpgradePopup(false)}
        message={upgradeMessage}
      />

      {/* Sources Modal */}
      {
        showSourcesModal && searchResults.length > 0 && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600 rounded-lg text-white">
                    <Globe size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Data Sources</h2>
                    <p className="text-sm text-gray-500">References used for generation</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSourcesModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-md"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 max-h-[60vh] overflow-y-auto">
                <div className="flex flex-wrap gap-3">
                  {searchResults.map((source, idx) => {
                    const url = typeof source === 'string' ? source : source.url;
                    let hostname = url;
                    try {
                      hostname = new URL(url).hostname.replace(/^www\./, '');
                    } catch (e) {
                      console.error('Invalid URL:', url);
                    }

                    return (
                      <a
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-all group bg-white shadow-sm hover:shadow-md text-decoration-none"
                      >
                        <div className="flex-shrink-0 text-blue-500 bg-blue-100 p-1 rounded-md group-hover:bg-white group-hover:text-blue-600 transition-all">
                          <Globe size={14} />
                        </div>
                        <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">
                          {source.title}
                        </span>
                        <ExternalLink size={12} className="text-gray-400 group-hover:text-blue-500" />
                      </a>
                    );
                  })}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setShowSourcesModal(false)}
                  className="px-5 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      <FormatSelectionModal
        isOpen={showFormatModal}
        currentFormat={userSelectedFormat}
        onClose={() => setShowFormatModal(false)}
        onSelect={(format) => {
          setUserSelectedFormat(format);
          setShowFormatModal(false);
          setIsChatOpen(true);
        }}
      />

      {/* Premium Download Modal */}
      {showPremiumDownloadModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white max-w-md w-full p-6 rounded-2xl shadow-xl border border-gray-100 flex flex-col items-center text-center animate-in fade-in zoom-in duration-200">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-4">
              <AlertCircle size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Dataset Limit</h3>
            <p className="text-sm text-gray-600 mb-6">
              Your first 500 rows are downloading for free! Since your dataset has <strong>{csvData.length}</strong> rows, you can download the full file for a one-time fee of ₹99.
            </p>
            <div className="w-full flex flex-col gap-3">
              <div className="w-full">
                <RazorpayCheckout
                  amount={99}
                  label="Download Full Dataset for ₹99"
                  onSuccess={() => {
                    setHasPaidPremiumDownload(true);
                    setShowPremiumDownloadModal(false);
                    if (pendingDownloadFormat) {
                      setTimeout(() => handleDownload(pendingDownloadFormat), 100);
                    }
                  }}
                  user={user}
                />
              </div>
              <button
                onClick={() => {
                  setShowPremiumDownloadModal(false);
                  setPendingDownloadFormat(null);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors border border-gray-200"
              >
                No thanks, I'm good
              </button>
            </div>
            <p className="mt-4 text-xs text-gray-400">If you make changes, your sliced 500-row file will be available dynamically next time you download.</p>
          </div>
        </div>
      )}

      {/* Auth Modal Overlay */}
      {showAuthModal && !user && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-lg animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setShowAuthModal(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-200 transition-colors"
            >
              <X size={28} />
            </button>
            <Auth
              setUser={() => { }}
              isModal={true}
              onSuccess={() => setShowAuthModal(false)}
            />
          </div>
        </div>
      )}

      {/* Tawk.to Live Chat — bottom-left so it doesn't overlap the AI panel */}
      <TawkToChat />
    </div >
  );
};

export default CSVEditor;

